import { Fragment, memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { isStaffCategory } from "../auth/permissions";
import { useNavigate } from "react-router-dom";
import { auth, db } from "../firebase";
import { signOut } from "firebase/auth";

import { AnimatePresence, motion } from "framer-motion";
import { Toast } from "primereact/toast";

import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  serverTimestamp,
} from "firebase/firestore";

// Iso3 converter imports
import countries from "i18n-iso-countries";
import enLocale from "i18n-iso-countries/langs/en.json";
countries.registerLocale(enLocale);

import Select from "react-select";

import { useAuth } from "../auth/AuthProvider";
import {
  DEFAULT_UBO_DOCUMENT_SCOPE,
  SUPPORTING_DOCUMENT_POLICY_COLLECTION,
  normalizeUboDocumentScope,
  sanitizeSupportingDocumentPolicy,
  supportingDocRisksForModel,
} from "../utils/sowDocumentPolicy";
import {
  COUNTRY_RISK_IMPACT_OPTIONS,
  COUNTRY_RISK_ORDER,
  COUNTRY_RISK_SOURCE_RULE_OPTIONS,
  DEFAULT_COUNTRY_RISK_RULES,
  countryRiskFromSourceKeys,
  normalizeIndividualCombination,
  normalizeCountryRiskImpact,
  normalizeUboCountryRiskScope,
  sanitizeCountryRiskRules,
  UBO_COUNTRY_RISK_SCOPE_ALL_UBOS,
  UBO_COUNTRY_RISK_SCOPE_THRESHOLD_AND_DIRECT,
  UBO_COUNTRY_RISK_SCOPE_THRESHOLD_ONLY,
} from "../utils/countryRiskRules";
import {
  DEFAULT_RELATIONSHIP_RISK_SETTINGS,
  RELATIONSHIP_RISK_DEFAULT_DOC_ID,
  RELATIONSHIP_RISK_MODEL_2_LEVEL,
  RELATIONSHIP_RISK_MODEL_3_LEVEL,
  RELATIONSHIP_RISK_MODEL_OPTIONS,
  RELATIONSHIP_RISK_SETTINGS_COLLECTION,
  normalizeRelationshipRiskImpactForModel,
  normalizeRelationshipRiskModel,
  relationshipRiskFactorsForModel,
  relationshipRiskImpactsForModel,
  relationshipRuleSummary,
  sanitizeRelationshipRiskRules,
  sanitizeRelationshipRiskSettings,
} from "../utils/relationshipRiskRules";

import "./css/AdminUsersPage.css";

const DEFAULT_RISK_OPTION_ID = "__default__";

const orgSelectStyles = {
  control: (base) => ({
    ...base,
    minHeight: "34px",
    height: "34px",
    minWidth: "200px",
    borderRadius: "0.375rem",
    borderColor: "rgba(255,255,255,0.45)",
    backgroundColor: "rgba(255,255,255,0.15)",
    boxShadow: "none",
  }),
  valueContainer: (base) => ({ ...base, padding: "0 10px" }),
  placeholder: (base) => ({ ...base, color: "rgba(255,255,255,0.7)", fontWeight: 300, fontSize: 13 }),
  singleValue: (base) => ({ ...base, color: "#fff", fontSize: 13 }),
  input: (base) => ({ ...base, color: "#fff", margin: 0, padding: 0 }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected
      ? "rgba(155,175,165,0.35)"
      : state.isFocused
      ? "rgba(155,175,165,0.18)"
      : "transparent",
    color: "#2f3a4a",
    cursor: "pointer",
    fontSize: 13,
  }),
  indicatorSeparator: () => ({ display: "none" }),
  dropdownIndicator: (base) => ({ ...base, color: "rgba(255,255,255,0.7)", padding: "0 6px" }),
  menu: (base) => ({ ...base, zIndex: 50 }),
};

const countryMultiSelectStyles = {
  container: (base) => ({
    ...base,
    flex: "1 1 auto",
    display: "flex",
    flexDirection: "column",
  }),
  control: (base, state) => ({
    ...base,
    minHeight: 132,
    height: "100%",
    alignItems: "flex-start",
    position: "relative",
    borderColor: state.isFocused ? "#9BB0A5" : "#DFE1E5",
    boxShadow: state.isFocused ? "0 0 0 0.15rem rgba(155, 176, 165, 0.18)" : "none",
    backgroundColor: state.selectProps.inputBackground || "#fff",
    fontSize: 13,
  }),
  valueContainer: (base) => ({
    ...base,
    alignItems: "flex-start",
    padding: "6px 6px",
    overflowY: "auto",
  }),
  multiValue: (base, state) => ({
    ...base,
    backgroundColor: state.data?.overrideInfo
      ? state.data.overrideInfo.direction === "downgrade"
        ? "#F2E2E6"
        : "#ECF2FA"
      : "#EEF3F0",
    borderRadius: 6,
  }),
  multiValueLabel: (base, state) => ({
    ...base,
    color: state.data?.overrideInfo ? "#545D6D" : "#4C5465",
    fontSize: 12,
    padding: "3px 5px",
  }),
  multiValueRemove: (base, state) => ({
    ...base,
    display: state.selectProps.isDisabled ? "none" : "flex",
    color: "#789085",
    ":hover": {
      backgroundColor: "#DDE7E1",
      color: "#4C5465",
    },
  }),
  placeholder: (base) => ({ ...base, color: "#9AA3AF", fontSize: 13 }),
  input: (base) => ({ ...base, color: "#4C5465", fontSize: 13 }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected
      ? "rgba(155,175,165,0.35)"
      : state.isFocused
      ? "rgba(155,175,165,0.18)"
      : "#fff",
    color: "#2f3a4a",
    cursor: "pointer",
    fontSize: 13,
  }),
  indicatorSeparator: () => ({ display: "none" }),
  indicatorsContainer: (base) => ({
    ...base,
    alignItems: "center",
    backgroundColor: "#FCFCFC",
  }),
  clearIndicator: (base, state) => ({
    ...base,
    display: state.selectProps.isDisabled ? "none" : "flex",
    position: "absolute",
    top: 2,
    right: 3,
    padding: 2,
    zIndex: 2,
  }),
  dropdownIndicator: (base, state) => ({
    ...base,
    display: state.selectProps.isDisabled ? "none" : "flex",
    alignSelf: "center",
    padding: "0 4px",
  }),
  menu: (base) => ({ ...base, zIndex: 60 }),
};

// ******************* VARIABLES ********************

const pageAnim = {
  initial: { opacity: 0, y: 10 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -10 },
};

const collapseAnim = {
  initial: { height: 0, opacity: 0 },
  animate: { height: "auto", opacity: 1 },
  exit: { height: 0, opacity: 0 },
  transition: { duration: 0.2, ease: "easeInOut" },
};

// ******************* HELPERS ********************

function stableStringify(value) {
  if (Array.isArray(value)) {
    return `[${value.map((item) => stableStringify(item)).join(",")}]`;
  }
  if (value && typeof value === "object") {
    return `{${Object.keys(value)
      .sort()
      .map((key) => `${JSON.stringify(key)}:${stableStringify(value[key])}`)
      .join(",")}}`;
  }
  return JSON.stringify(value);
}

function uniqIso3(arr) {
  const out = [];
  const seen = new Set();
  for (const raw of arr || []) {
    const v = String(raw || "")
      .trim()
      .toUpperCase();
    if (!v) continue;
    if (v.length !== 3) continue;
    if (!seen.has(v)) {
      seen.add(v);
      out.push(v);
    }
  }
  return out.sort();
}

function parseIso3Textarea(text) {
  return uniqIso3(
    String(text || "")
      .split(/[\s,;\n\r\t]+/g)
      .map((x) => x.trim())
      .filter(Boolean)
  );
}

function iso3FromName(name) {
  const s = String(name || "").trim();
  if (!s) return "";
  const iso3 = countries.getAlpha3Code(s, "en");
  return (iso3 || "").toUpperCase();
}

function nameFromIso3(iso3) {
  const s = String(iso3 || "")
    .trim()
    .toUpperCase();
  if (s.length !== 3) return "";
  const iso2 = countries.alpha3ToAlpha2(s);
  if (!iso2) return "";
  return countries.getName(iso2, "en") || "";
}

function riskLabel(risk) {
  if (risk === "high") return "High";
  if (risk === "medium") return "Medium";
  return "Standard";
}

function overrideDirection(defaultRisk, customRisk) {
  const defaultRank = COUNTRY_RISK_ORDER[defaultRisk] || 1;
  const customRank = COUNTRY_RISK_ORDER[customRisk] || 1;
  if (customRank < defaultRank) return "downgrade";
  if (customRank > defaultRank) return "upgrade";
  return "same";
}

function countryOptionLabel(option, { context }) {
  if (context === "value") return option.label;
  return (
    <div className="d-flex align-items-center justify-content-between gap-2">
      <span>{option.label}</span>
      <span className="text-muted" style={{ fontSize: 11 }}>
        {option.iso3}
      </span>
    </div>
  );
}

function RiskExpandableIcon({ open }) {
  return (
    <span className={`riskExpandableIcon${open ? " is-open" : ""}`} aria-hidden="true">
      <i className="bi bi-chevron-down" />
    </span>
  );
}

function AnimatedCollapse({ children }) {
  return (
    <motion.div
      initial={collapseAnim.initial}
      animate={collapseAnim.animate}
      exit={collapseAnim.exit}
      transition={collapseAnim.transition}
      style={{ overflow: "hidden" }}
    >
      {children}
    </motion.div>
  );
}

function CountryRiskRulesPanelHeader({ open, onToggle }) {
  return (
    <button
      type="button"
      className="relationshipRiskRulesPanelHead countryRiskRulesPanelHead riskExpandableHeader"
      aria-expanded={open}
      onClick={onToggle}
    >
      <div>
        <div className="fw-semibold" style={{ color: "#4C5465", fontSize: 14 }}>
          Country risk calculation rules
        </div>
        <div className="small text-muted">
          Define how country lists and source matches feed into geographic risk outcomes.
        </div>
      </div>
      <RiskExpandableIcon open={open} />
    </button>
  );
}

function CountryRiskRulesPanel({
  rules,
  setRules,
  disabled,
  open,
  onToggle,
  isTwoLevelFinalRiskModel = false,
}) {
  const cleanRules = sanitizeCountryRiskRules(rules);
  const uboCountryRiskScope = normalizeUboCountryRiskScope(cleanRules.entityUboScope);
  const sourceCount = COUNTRY_RISK_SOURCE_RULE_OPTIONS.length;
  const individualCombination = normalizeIndividualCombination(cleanRules.individualCombination);

  function setSourceImpact(sourceKey, impact) {
    const normalized = normalizeCountryRiskImpact(impact, "none");
    setRules((prev) => {
      const clean = sanitizeCountryRiskRules(prev);
      const updatedFactors = { ...clean.factors, [sourceKey]: { impact: normalized } };
      if (normalized !== "none") {
        if (sourceKey === "baselScore") {
          updatedFactors.baselScoreMedium = { impact: "none" };
          updatedFactors.baselScoreHigh = { impact: "none" };
        } else if (sourceKey === "baselScoreMedium" || sourceKey === "baselScoreHigh") {
          updatedFactors.baselScore = { impact: "none" };
        }
      }
      return { ...clean, factors: updatedFactors };
    });
  }

  function setThreshold(key, value) {
    setRules((prev) => ({
      ...sanitizeCountryRiskRules(prev),
      thresholds: {
        ...sanitizeCountryRiskRules(prev).thresholds,
        [key]: value,
      },
    }));
  }

  function setThresholdEnabled(key, checked) {
    setRules((prev) => ({
      ...sanitizeCountryRiskRules(prev),
      thresholds: {
        ...sanitizeCountryRiskRules(prev).thresholds,
        [key]: checked,
      },
    }));
  }

  function setIndividualCombinationPatch(patch) {
    setRules((prev) => {
      const clean = sanitizeCountryRiskRules(prev);
      return {
        ...clean,
        individualCombination: normalizeIndividualCombination({
          ...clean.individualCombination,
          ...patch,
        }),
      };
    });
  }

  function setUboCountryRiskScope(scope) {
    setRules((prev) => {
      const clean = sanitizeCountryRiskRules(prev);
      return {
        ...clean,
        entityUboScope: normalizeUboCountryRiskScope(scope),
      };
    });
  }

  const PREVIEW_LEVELS = ["low", "medium", "high"];
  const PREVIEW_LABEL = { low: "Standard", medium: "Medium", high: "High" };

  function individualPreviewResult(nationalityLevel, domicileLevel) {
    const order = { low: 1, medium: 2, high: 3 };
    const toLevel = (n) => (n >= 3 ? "high" : n >= 2 ? "medium" : "low");
    const nat = order[nationalityLevel] || 1;
    const dom = order[domicileLevel] || 1;

    if (individualCombination.method === "domicile_only") return domicileLevel;
    if (individualCombination.method === "nationality_only") return nationalityLevel;

    if (individualCombination.method === "weighted_others") {
      if (individualCombination.weightedOtherMethod === "equal_round_up") {
        return toLevel(Math.ceil((nat + dom) / 2));
      }

      const primary = individualCombination.primaryCountry === "domicile" ? dom : nat;
      const secondary = individualCombination.primaryCountry === "domicile" ? nat : dom;
      const result = secondary > primary ? Math.min(3, primary + 1) : primary;
      return toLevel(result);
    }

    return toLevel(Math.max(nat, dom));
  }

  function setCombinedThreshold(key, value) {
    setRules((prev) => {
      const cleanPrev = sanitizeCountryRiskRules(prev);
      return {
        ...cleanPrev,
        thresholds: {
          ...cleanPrev.thresholds,
          combinedFactorsToHigh: {
            ...(cleanPrev.thresholds?.combinedFactorsToHigh || {}),
            [key]: value,
          },
        },
      };
    });
  }

  return (
    <div className="relationshipRiskRulesPanel mb-3">
      <CountryRiskRulesPanelHeader open={open} onToggle={onToggle} />

      <AnimatePresence initial={false}>
        {open && (
          <AnimatedCollapse>
            <div className="countryGeneralRulesCard">
              <div className="countryRiskRulesNumberedBlock">
                <div className="countryRiskRulesNumberedTitle">
                  <span className="countryRiskRulesNumberBadge">1</span>
                  <span>General calculation rules</span>
                </div>
                <div className="countryRiskRulesSubtitle countryRiskRulesSubtitle--general">
                  Choose how each default source contributes to country risk.
                </div>
              </div>

              <div className="relationshipRiskMatrixWrap">
                <table className="relationshipRiskMatrix">
                  <thead>
                    <tr>
                      <th>Source list</th>
                      {COUNTRY_RISK_IMPACT_OPTIONS.map((impact) => (
                        <th
                          key={impact.key}
                          className={
                            isTwoLevelFinalRiskModel &&
                            (impact.key === "medium" || impact.key === "immediate_medium")
                              ? "relationshipRiskMatrixMediumColumn"
                              : ""
                          }
                        >
                          {impact.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {COUNTRY_RISK_SOURCE_RULE_OPTIONS.map((source) => {
                      const selectedImpact = normalizeCountryRiskImpact(
                        cleanRules.factors?.[source.key]?.impact,
                        DEFAULT_COUNTRY_RISK_RULES.factors[source.key]?.impact
                      );
                      let rowLabel;
                      if (source.key === "cpiScore") {
                        rowLabel = (
                          <>
                            {"CPI score <= "}
                            <input
                              type="number"
                              min="0"
                              max="100"
                              value={cleanRules.thresholds?.cpiScoreAtOrBelow ?? 40}
                              disabled={disabled}
                              onChange={(e) => setThreshold("cpiScoreAtOrBelow", e.target.value)}
                              style={{
                                width: 56,
                                border: "1px solid #DDE5EA",
                                borderRadius: 6,
                                padding: "2px 6px",
                                color: "#4C5465",
                                fontSize: "inherit",
                              }}
                            />
                          </>
                        );
                      } else if (source.key === "baselScore") {
                        rowLabel = (
                          <>
                            {"Basel AML Index >= "}
                            <input
                              type="number"
                              min="0"
                              max="10"
                              step="0.1"
                              value={cleanRules.thresholds?.baselScoreAtOrAbove ?? 6}
                              disabled={disabled}
                              onChange={(e) => setThreshold("baselScoreAtOrAbove", e.target.value)}
                              style={{
                                width: 56,
                                border: "1px solid #DDE5EA",
                                borderRadius: 6,
                                padding: "2px 6px",
                                color: "#4C5465",
                                fontSize: "inherit",
                              }}
                            />
                          </>
                        );
                      } else if (source.key === "baselScoreMedium") {
                        rowLabel = "Basel AML Index 5.5 – 7";
                      } else if (source.key === "baselScoreHigh") {
                        rowLabel = "Basel AML Index > 7";
                      } else {
                        rowLabel = source.label;
                      }
                      return (
                        <tr key={source.key}>
                          <td>
                            <span className="relationshipRiskRuleTitle">{rowLabel}</span>
                          </td>
                          {COUNTRY_RISK_IMPACT_OPTIONS.map((impact) => (
                            <td
                              key={impact.key}
                              className={
                                isTwoLevelFinalRiskModel &&
                                (impact.key === "medium" || impact.key === "immediate_medium")
                                  ? "relationshipRiskMatrixMediumColumn"
                                  : ""
                              }
                            >
                              <label className="relationshipRiskImpactOption">
                                <input
                                  className="form-check-input m-0 adminRiskSettingsCheckbox"
                                  type="radio"
                                  name={`country-risk-rule-${source.key}`}
                                  checked={selectedImpact === impact.key}
                                  disabled={disabled}
                                  onChange={() => setSourceImpact(source.key, impact.key)}
                                />
                                <span>{impact.label}</span>
                              </label>
                            </td>
                          ))}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

      <div className="relationshipRiskThresholdBox">
        <label className="relationshipRiskThresholdLabel">
          <span className="relationshipRiskThresholdText">
            <input
              className="form-check-input m-0 adminRiskSettingsCheckbox"
              type="checkbox"
              checked={!!cleanRules.thresholds?.mediumFactorsToMediumEnabled}
              disabled={disabled}
              onChange={(e) => setThresholdEnabled("mediumFactorsToMediumEnabled", e.target.checked)}
            />
            <span className="relationshipRiskThresholdCopy">
              <span className="relationshipRiskThresholdTitle">Medium factor threshold</span>
              <span className="relationshipRiskThresholdSub">
                Set Medium when medium and high factors are at least
              </span>
            </span>
          </span>
          <input
            type="number"
            min="1"
            max={sourceCount}
            value={cleanRules.thresholds?.mediumFactorsToMedium || 1}
            disabled={disabled || !cleanRules.thresholds?.mediumFactorsToMediumEnabled}
            onChange={(e) => setThreshold("mediumFactorsToMedium", e.target.value)}
          />
        </label>

        <label className="relationshipRiskThresholdLabel">
          <span className="relationshipRiskThresholdText">
            <input
              className="form-check-input m-0 adminRiskSettingsCheckbox"
              type="checkbox"
              checked={!!cleanRules.thresholds?.mediumFactorsToHighEnabled}
              disabled={disabled}
              onChange={(e) => setThresholdEnabled("mediumFactorsToHighEnabled", e.target.checked)}
            />
            <span className="relationshipRiskThresholdCopy">
              <span className="relationshipRiskThresholdTitle">Medium factor escalation</span>
              <span className="relationshipRiskThresholdSub">Set High when medium factors are at least</span>
            </span>
          </span>
          <input
            type="number"
            min="1"
            max={sourceCount}
            value={cleanRules.thresholds?.mediumFactorsToHigh || 1}
            disabled={disabled || !cleanRules.thresholds?.mediumFactorsToHighEnabled}
            onChange={(e) => setThreshold("mediumFactorsToHigh", e.target.value)}
          />
        </label>

        <label className="relationshipRiskThresholdLabel">
          <span className="relationshipRiskThresholdText">
            <input
              className="form-check-input m-0 adminRiskSettingsCheckbox"
              type="checkbox"
              checked={!!cleanRules.thresholds?.highFactorsToHighEnabled}
              disabled={disabled}
              onChange={(e) => setThresholdEnabled("highFactorsToHighEnabled", e.target.checked)}
            />
            <span className="relationshipRiskThresholdCopy">
              <span className="relationshipRiskThresholdTitle">High factor threshold</span>
              <span className="relationshipRiskThresholdSub">Set High when high factors are at least</span>
            </span>
          </span>
          <input
            type="number"
            min="1"
            max={sourceCount}
            value={cleanRules.thresholds?.highFactorsToHigh || 1}
            disabled={disabled || !cleanRules.thresholds?.highFactorsToHighEnabled}
            onChange={(e) => setThreshold("highFactorsToHigh", e.target.value)}
          />
        </label>

        <label className="relationshipRiskThresholdLabel relationshipRiskThresholdLabel--combined">
          <span className="relationshipRiskThresholdText">
            <input
              className="form-check-input m-0 adminRiskSettingsCheckbox"
              type="checkbox"
              checked={!!cleanRules.thresholds?.combinedFactorsToHigh?.enabled}
              disabled={disabled}
              onChange={(e) => setCombinedThreshold("enabled", e.target.checked)}
            />
            <span className="relationshipRiskThresholdCopy">
              <span className="relationshipRiskThresholdTitle">Combined factor escalation</span>
              <span className="relationshipRiskThresholdSub">Set High when both thresholds are met</span>
            </span>
          </span>
          <span className="relationshipRiskThresholdPair">
            <span>
              <span>High</span>
              <input
                type="number"
                min="1"
                max={sourceCount}
                value={cleanRules.thresholds?.combinedFactorsToHigh?.highFactors || 1}
                disabled={disabled || !cleanRules.thresholds?.combinedFactorsToHigh?.enabled}
                onChange={(e) => setCombinedThreshold("highFactors", e.target.value)}
              />
            </span>
            <span>
              <span>Medium</span>
              <input
                type="number"
                min="1"
                max={sourceCount}
                value={cleanRules.thresholds?.combinedFactorsToHigh?.mediumFactors || 1}
                disabled={disabled || !cleanRules.thresholds?.combinedFactorsToHigh?.enabled}
                onChange={(e) => setCombinedThreshold("mediumFactors", e.target.value)}
              />
            </span>
          </span>
        </label>
      </div>
      </div>

      <div className="countryCombinationBox">
        <div className="countryCombinationBoxHead countryRiskRulesNumberedBlock">
          <span className="countryRiskRulesSubsectionTitle">
            <span className="countryRiskRulesNumberBadge">2</span>
            Rules for individuals
          </span>
          <span className="countryRiskRulesSubtitle">
            How nationality and domicile country risks are merged into one geo risk level
          </span>
        </div>

        <div className="countryCombinationCards">
          {[
            {
              key: "weighted_worst",
              title: "Weighted (Worst Wins)",
              sub: "Default",
            },
            {
              key: "domicile_only",
              title: "Domicile only",
              sub: "Use domicile country risk only",
            },
            {
              key: "nationality_only",
              title: "Nationality only",
              sub: "Use nationality country risk only",
            },
            {
              key: "weighted_others",
              title: "Weighted (Others)",
              sub: "Configure alternative weighting",
            },
          ].map(({ key, title, sub }) => (
            <label
              key={key}
              className={`countryCombinationCard${
                individualCombination.method === key ? " countryCombinationCard--active" : ""
              }${disabled ? " countryCombinationCard--disabled" : ""}`}
            >
              <input
                type="radio"
                name="individual-country-risk-method"
                value={key}
                checked={individualCombination.method === key}
                disabled={disabled}
                onChange={() => setIndividualCombinationPatch({ method: key })}
                style={{ display: "none" }}
              />
              <span className="countryCombinationCardTitle">{title}</span>
              <span className="countryCombinationCardSub">{sub}</span>
            </label>
          ))}
        </div>

        {individualCombination.method === "weighted_others" && (
          <div className="countryCombinationWeightedSection">
            <div className="countryCombinationRow">
              <span className="countryCombinationRowLabel">Weighted method</span>
              <div className="countryCombinationRowOptions">
                <label className="countryCombinationRadio">
                  <input
                    className="form-check-input m-0 adminRiskSettingsCheckbox"
                    type="radio"
                    name="individual-weighted-other-method"
                    checked={individualCombination.weightedOtherMethod === "equal_round_up"}
                    disabled={disabled}
                    onChange={() => setIndividualCombinationPatch({ weightedOtherMethod: "equal_round_up" })}
                  />
                  <span>Equal weight (round up)</span>
                </label>
                <label className="countryCombinationRadio">
                  <input
                    className="form-check-input m-0 adminRiskSettingsCheckbox"
                    type="radio"
                    name="individual-weighted-other-method"
                    checked={individualCombination.weightedOtherMethod === "primary_raise"}
                    disabled={disabled}
                    onChange={() => setIndividualCombinationPatch({ weightedOtherMethod: "primary_raise" })}
                  />
                  <span>Primary country can raise result by 1 level</span>
                </label>
              </div>
            </div>
            {individualCombination.weightedOtherMethod === "primary_raise" ? (
              <div className="countryCombinationRow">
                <span className="countryCombinationRowLabel">Primary country</span>
                <div className="countryCombinationRowOptions">
                  {[
                    { key: "domicile", label: "Domicile" },
                    { key: "nationality", label: "Nationality" },
                  ].map(({ key, label }) => (
                    <label key={key} className="countryCombinationRadio">
                      <input
                        className="form-check-input m-0 adminRiskSettingsCheckbox"
                        type="radio"
                        name="individual-weighted-primary-country"
                        value={key}
                        checked={individualCombination.primaryCountry === key}
                        disabled={disabled}
                        onChange={() => setIndividualCombinationPatch({ primaryCountry: key })}
                      />
                      <span>{label}</span>
                    </label>
                  ))}
                </div>
              </div>
            ) : null}
          </div>
        )}

        <div className="countryCombinationPreviewWrap">
          <div className="countryCombinationPreviewHead">
            <div>
              <div className="countryCombinationPreviewTitle">Result preview</div>
            </div>
          </div>
          <div className="countryCombinationPreviewMatrix" role="table" aria-label="Individual geo risk result preview">
            <div className="countryCombinationPreviewCorner" />
            <div className="countryCombinationPreviewAxis countryCombinationPreviewAxis--top">
              Domicile risk
            </div>
            <div className="countryCombinationPreviewAxis countryCombinationPreviewAxis--left">
              Nationality risk
            </div>
            <div className="countryCombinationPreviewGrid">
              <div className="countryCombinationPreviewGridHeader" />
              {PREVIEW_LEVELS.map((level) => (
                <div
                  key={level}
                  className={`countryCombinationPreviewColHead${
                    isTwoLevelFinalRiskModel && level === "medium"
                      ? " countryCombinationPreviewMediumLabel"
                      : ""
                  }`}
                >
                  {PREVIEW_LABEL[level]}
                </div>
              ))}
              {PREVIEW_LEVELS.map((natLevel) => (
                <Fragment key={natLevel}>
                  <div
                    className={`countryCombinationPreviewRowHead${
                      isTwoLevelFinalRiskModel && natLevel === "medium"
                        ? " countryCombinationPreviewMediumLabel"
                        : ""
                    }`}
                  >
                    {PREVIEW_LABEL[natLevel]}
                  </div>
                  {PREVIEW_LEVELS.map((domLevel) => {
                    const result = individualPreviewResult(natLevel, domLevel);
                    return (
                      <div
                        key={`${natLevel}-${domLevel}`}
                        className="countryCombinationPreviewCell"
                        role="cell"
                      >
                        <span
                          className={`countryCombinationPreviewBadge countryCombinationPreviewBadge--${result}${
                            isTwoLevelFinalRiskModel && result === "medium"
                              ? " countryCombinationPreviewMediumLabel"
                              : ""
                          }`}
                        >
                          {PREVIEW_LABEL[result] || "—"}
                        </span>
                      </div>
                    );
                  })}
                </Fragment>
              ))}
            </div>
          </div>
        </div>
      </div>

        <div className="countryUboRulesBox">
          <div className="countryCombinationBoxHead countryRiskRulesNumberedBlock">
            <span className="countryRiskRulesSubsectionTitle">
              <span className="countryRiskRulesNumberBadge">3</span>
              Rules for entities
            </span>
            <span className="countryRiskRulesSubtitle">
              How nationality and domicile country risks and UBOs combination are merged into one geo risk level
            </span>
          </div>

          <div className="countryUboScopeBlock">
            <div className="relationshipRiskModelToggleHeading countryUboScopeHeading">
              UBOs to Consider
            </div>
            <div
              className="countryCombinationCards countryUboScopeCards"
              role="radiogroup"
              aria-label="UBOs to consider"
            >
            {[
              {
                value: UBO_COUNTRY_RISK_SCOPE_THRESHOLD_ONLY,
                label: "UBOs meeting thresholds",
                detail: "API-qualified UBOs only",
              },
              {
                value: UBO_COUNTRY_RISK_SCOPE_THRESHOLD_AND_DIRECT,
                label: "UBOs meeting thresholds + Direct shareholder",
                detail: "Add direct shareholders",
              },
              {
                value: UBO_COUNTRY_RISK_SCOPE_ALL_UBOS,
                label: "All UBOs",
                detail: "All individual UBOs",
              },
            ].map((option) => (
              <label
                key={option.value}
                className={`countryCombinationCard ${
                  uboCountryRiskScope === option.value ? "countryCombinationCard--active" : ""
                }${disabled ? " countryCombinationCard--disabled" : ""}`}
              >
                <input
                  type="radio"
                  name="ubo-country-risk-scope"
                  checked={uboCountryRiskScope === option.value}
                  disabled={disabled}
                  onChange={() => setUboCountryRiskScope(option.value)}
                  style={{ display: "none" }}
                />
                <span className="countryCombinationCardTitle">{option.label}</span>
                <span className="countryCombinationCardSub">{option.detail}</span>
              </label>
            ))}
            </div>
          </div>

          <div className="countryUboFixedMethod">
            <span className="countryUboFixedMethodLabel">
              Multiple UBO Aggregation Method:
            </span>
            <span className="countryUboFixedMethodValue">
              Worst win <span className="countryUboFixedMethodNote">fixed method</span>
            </span>
          </div>
          <div className="countryUboFixedMethod">
            <span className="countryUboFixedMethodLabel">
              Final entity risk aggregation:
            </span>
            <span className="countryUboFixedMethodValue">
              Worst of incorporation country risk and UBO country risk{" "}
              <span className="countryUboFixedMethodNote">fixed method</span>
            </span>
          </div>
        </div>
          </AnimatedCollapse>
        )}
      </AnimatePresence>
    </div>
  );
}

const CountryRiskListEditor = memo(function CountryRiskListEditor({
  title,
  count,
  text,
  options,
  onTextChange,
  disabled,
  isAdmin,
  advancedOpen,
  advancedKey,
  onToggleAdvanced,
  overrideMap = {},
  placeholder = "Search countries…",
  attention = false,
  inputBackground = "",
}) {
  const selectedOptions = useMemo(() => {
    const byIso3 = new Map(options.map((option) => [option.value, option]));
    return parseIso3Textarea(text).map((iso3) => {
      const found = byIso3.get(iso3);
      const overrideInfo = overrideMap[iso3] || null;
      if (found) return { ...found, overrideInfo };
      return { value: iso3, iso3, label: nameFromIso3(iso3) || iso3, overrideInfo };
    });
  }, [options, overrideMap, text]);

  return (
    <div className={`col-12 col-md-6 col-xl-4 d-flex flex-column ${attention ? "riskInputAttention" : ""}`}>
      <div className="d-flex align-items-center justify-content-between gap-2 mb-1">
        <div className="text-muted small">
          {title}
          {attention ? <span className="riskInputAttentionBadge">Input factor</span> : null}
        </div>
        <div className="text-muted small" style={{ fontSize: 9 }}>
          ({count} countries)
        </div>
      </div>

      <Select
        className="flex-grow-1 d-flex flex-column"
        isMulti
        isDisabled={disabled}
        closeMenuOnSelect={false}
        hideSelectedOptions={false}
        options={options}
        value={selectedOptions}
        onChange={(items) => {
          const isoList = uniqIso3((items || []).map((item) => item.value));
          onTextChange(isoList.join("\n"));
        }}
        styles={countryMultiSelectStyles}
        inputBackground={inputBackground}
        formatOptionLabel={countryOptionLabel}
        placeholder={placeholder}
        noOptionsMessage={() => "No country found"}
      />

      {isAdmin && (
        <button
          type="button"
          className="btn btn-link p-0 mt-2"
          style={{ fontSize: 12, color: "#879FB1", textDecoration: "none" }}
          onClick={() => onToggleAdvanced(advancedKey)}
        >
          {advancedOpen ? "Hide bulk ISO edit" : "Bulk edit ISO codes"}
        </button>
      )}

      {isAdmin && advancedOpen && (
        <textarea
          className="form-control mt-2"
          rows={7}
          value={text}
          onChange={(e) => onTextChange(e.target.value)}
          disabled={disabled}
          placeholder="ARE&#10;FRA&#10;..."
          style={{ fontSize: 13, backgroundColor: inputBackground || "#fff", opacity: 1 }}
        />
      )}
    </div>
  );
});

function CountryOverrideCompactCard({ rows, downgradeCount }) {
  if (!rows.length) return null;

  const badgeStyle = (kind) => ({
    background: kind === "downgrade" ? "#F2E2E6" : "#ECF2FA",
    color: "#545D6D",
    fontWeight: 500,
    fontSize: 10,
    border: "1px solid rgba(84, 93, 109, 0.08)",
    whiteSpace: "normal",
    textAlign: "left",
  });

  return (
    <div className="col-12 col-md-6 d-flex flex-column mx-auto">
      <div className="d-flex align-items-center justify-content-between gap-2 mb-1">
        <div className="text-muted small">
          {downgradeCount > 0 ? "Overrides • Warning" : "Overrides"}
        </div>
        <div className="text-muted small" style={{ fontSize: 9 }}>
          ({rows.length})
        </div>
      </div>

      <div
        className="rounded-3 border flex-grow-1"
        style={{
          minHeight: 132,
          background: downgradeCount > 0 ? "#FFF7ED" : "#F7FAFC",
          borderColor: downgradeCount > 0 ? "#F0C9A7" : "#DDE5EA",
          overflow: "hidden",
        }}
      >
        <div className="px-2 py-2 border-bottom" style={{ borderColor: downgradeCount > 0 ? "#F0C9A7" : "#DDE5EA" }}>
          <div className="fw-semibold" style={{ color: "#4C5465", fontSize: 12 }}>
            {downgradeCount > 0
              ? `${downgradeCount} lower default risk`
              : "Default risk overridden"}
          </div>
        </div>

        <div style={{ maxHeight: 220, overflowY: "auto" }}>
          {rows.map((row) => (
            <div
              key={`${row.iso3}-${row.customRisk}`}
              className="px-2 py-2 border-bottom"
              style={{ borderColor: "rgba(84, 93, 109, 0.08)" }}
            >
              <div className="d-flex align-items-baseline gap-1 mb-1" style={{ color: "#4C5465" }}>
                <span style={{ fontSize: 12, fontWeight: 600 }}>{row.country}</span>
                <span className="text-muted" style={{ fontSize: 10 }}>{row.iso3}</span>
              </div>

              <div className="d-flex align-items-center gap-1 flex-wrap">
                <span className="badge" style={badgeStyle(row.direction)}>
                  {riskLabel(row.defaultRisk)} ({row.sources.join(", ")})
                </span>
                <span style={{ color: "#9AA3AF", fontSize: 12 }}>→</span>
                <span className="badge" style={badgeStyle(row.direction)}>
                  {riskLabel(row.customRisk)}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function RiskLevelModelCard({
  isDefaultRiskSelection,
  rules,
  defaultRules,
  setRules,
  useDefaultModel = false,
  setUseDefaultModel,
  useDefaultRules = false,
  onSave,
  saving,
  canSave = true,
}) {
  const disabled = saving || (!isDefaultRiskSelection && (useDefaultModel || useDefaultRules));
  const cleanSettings = sanitizeRelationshipRiskSettings(
    !isDefaultRiskSelection && (useDefaultModel || useDefaultRules)
      ? defaultRules
      : rules
  );
  const activeModel = cleanSettings.activeModel;

  function setActiveModel(model) {
    setRules((prev) => ({
      ...sanitizeRelationshipRiskSettings(prev),
      activeModel: normalizeRelationshipRiskModel(model),
    }));
  }

  return (
    <div className="card shadow-sm mb-3 riskLevelModelCard">
      <div className="p-3 border-bottom d-flex align-items-start justify-content-between gap-3 flex-wrap">
        <div>
          <div className="fw-bold" style={{ color: "#4C5465" }}>
            Relationship risk level model
          </div>
          <div className="small text-muted">
            Choose the default risk scale used by relationship risk settings.
          </div>
        </div>
        <div className="relationshipRiskModelToggle" role="radiogroup" aria-label="Relationship risk model">
          {RELATIONSHIP_RISK_MODEL_OPTIONS.map((option) => (
            <label
              key={option.key}
              className={`relationshipRiskModelOption ${activeModel === option.key ? "is-selected" : ""}`}
            >
              <input
                type="radio"
                name="relationship-risk-model"
                checked={activeModel === option.key}
                disabled={disabled}
                onChange={() => setActiveModel(option.key)}
              />
              <span>
                <span className="relationshipRiskModelOptionTitle">{option.label}</span>
                <span className="relationshipRiskModelOptionDetail">{option.detail}</span>
              </span>
            </label>
          ))}
        </div>
      </div>
      <div className="p-3 d-flex align-items-center justify-content-between gap-2 flex-wrap">
        {!isDefaultRiskSelection ? (
          <div className="riskDocDefaultPolicyToggle riskDocDefaultPolicyToggle--withAction">
            <label htmlFor="relationshipRiskUseDefaultMode" className="riskDocDefaultPolicyToggleLabel">
              <input
                id="relationshipRiskUseDefaultMode"
                className="form-check-input m-0 adminRiskSettingsCheckbox"
                type="checkbox"
                checked={useDefaultModel || useDefaultRules}
                disabled={saving || useDefaultRules}
                onChange={(e) => setUseDefaultModel?.(e.target.checked)}
              />
              <span>Use default mode</span>
            </label>
          </div>
        ) : (
          <div className="small text-muted">
            Editing default industry risk categorisation
          </div>
        )}
        <button
          type="button"
          className="btn btn-save1"
          onClick={onSave}
          disabled={saving || !canSave}
        >
          {saving ? "Saving…" : "Save risk model"}
        </button>
      </div>
    </div>
  );
}

function RelationshipRiskRulesCard({
  isDefaultRiskSelection,
  selectedOrgId,
  rules,
  setRules,
  activeModelOverride = "",
  useDefaultRules,
  setUseDefaultRules,
  canPrefillDefault,
  onPrefillDefault,
  onSave,
  saving,
  canSave = true,
}) {
  const disabled = saving || (!isDefaultRiskSelection && useDefaultRules);
  const cleanSettings = sanitizeRelationshipRiskSettings(rules);
  const activeModel = activeModelOverride
    ? normalizeRelationshipRiskModel(activeModelOverride)
    : cleanSettings.activeModel;
  const activeRules = cleanSettings.models[activeModel];
  const factorOptions = relationshipRiskFactorsForModel(activeModel);
  const impactOptions = relationshipRiskImpactsForModel(activeModel);
  const isTwoLevelModel = activeModel === RELATIONSHIP_RISK_MODEL_2_LEVEL;

  function updateActiveRules(updater) {
    setRules((prev) => {
      const cleanPrev = sanitizeRelationshipRiskSettings(prev);
      const model = activeModelOverride
        ? normalizeRelationshipRiskModel(activeModelOverride)
        : cleanPrev.activeModel;
      const nextRule = sanitizeRelationshipRiskRules(
        updater(cleanPrev.models[model]),
        model
      );

      return {
        ...cleanPrev,
        models: {
          ...cleanPrev.models,
          [model]: nextRule,
        },
      };
    });
  }

  function setFactorImpact(key, impact) {
    updateActiveRules((prev) => ({
        ...prev,
        factors: {
          ...(prev.factors || {}),
          [key]: { impact },
        },
      }));
  }

  function setThreshold(key, value) {
    updateActiveRules((prev) => ({
        ...prev,
        thresholds: {
          ...(prev.thresholds || {}),
          [key]: value,
        },
      }));
  }

  function setThresholdEnabled(key, checked) {
    updateActiveRules((prev) => ({
        ...prev,
        thresholds: {
          ...(prev.thresholds || {}),
          [key]: checked,
        },
      }));
  }

  function setCombinedThreshold(key, value) {
    updateActiveRules((prev) => ({
        ...prev,
        thresholds: {
          ...(prev.thresholds || {}),
          combinedFactorsToHigh: {
            ...(prev.thresholds?.combinedFactorsToHigh || {}),
            [key]: value,
          },
        },
      }));
  }

  return (
    <div className="card shadow-sm mb-3 relationshipRiskRulesCard">
      <div className="p-3 border-bottom d-flex align-items-start justify-content-between gap-3 flex-wrap">
        <div>
          <div className="fw-bold" style={{ color: "#4C5465" }}>
            Relationship risk calculation rules
          </div>
          <div className="small text-muted">
            Rules for the selected risk level model. Stored in{" "}
            <code>
              {isDefaultRiskSelection
                ? `${RELATIONSHIP_RISK_SETTINGS_COLLECTION}/${RELATIONSHIP_RISK_DEFAULT_DOC_ID}`
                : `${RELATIONSHIP_RISK_SETTINGS_COLLECTION}/${selectedOrgId}`}
            </code>
            {!isDefaultRiskSelection &&
              `. If the default rules are enabled, calculation uses ${RELATIONSHIP_RISK_SETTINGS_COLLECTION}/${RELATIONSHIP_RISK_DEFAULT_DOC_ID}.`}
          </div>
        </div>
      </div>

      <div className="p-3">
        <div className="relationshipRiskRulesPanel">
          <div className="relationshipRiskRulesPanelHead">
            <div>
              <div className="fw-semibold" style={{ color: "#4C5465", fontSize: 14 }}>
                Factor impact
              </div>
              <div className="small text-muted">
                {isTwoLevelModel
                  ? "Immediate High forces High alone. Risk factors and High factors add up using the thresholds below."
                  : "Immediate High forces High alone. High and Medium factors add up using the thresholds below."}
              </div>
            </div>
          </div>

          <div className="relationshipRiskMatrixWrap">
            <table className="relationshipRiskMatrix">
              <thead>
                <tr>
                  <th>Risk factor</th>
                  {impactOptions.map((impact) => (
                    <th key={impact.key}>{impact.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {factorOptions.map((option) => {
                  const selectedImpact = normalizeRelationshipRiskImpactForModel(activeRules.factors?.[option.key]?.impact, activeModel);
                  return (
                    <tr key={option.key}>
                      <td>
                        <span className="relationshipRiskRuleTitle">{option.label}</span>
                      </td>
                      {impactOptions.map((impact) => (
                        <td key={impact.key}>
                          <label className="relationshipRiskImpactOption">
                            <input
                              className="form-check-input m-0 adminRiskSettingsCheckbox"
                              type="radio"
                              name={`relationship-risk-impact-${option.key}`}
                              checked={selectedImpact === impact.key}
                              disabled={disabled}
                              onChange={() => setFactorImpact(option.key, impact.key)}
                            />
                            <span>{impact.label}</span>
                          </label>
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="relationshipRiskThresholdBox">
            {!isTwoLevelModel ? (
              <label className="relationshipRiskThresholdLabel">
                <span className="relationshipRiskThresholdText">
                  <input
                    className="form-check-input m-0 adminRiskSettingsCheckbox"
                    type="checkbox"
                    checked={!!activeRules.thresholds?.mediumFactorsToMediumEnabled}
                    disabled={disabled}
                    onChange={(e) => setThresholdEnabled("mediumFactorsToMediumEnabled", e.target.checked)}
                  />
                  <span className="relationshipRiskThresholdCopy">
                    <span className="relationshipRiskThresholdTitle">Medium factor threshold</span>
                    <span className="relationshipRiskThresholdSub">Set Medium when medium factors are at least</span>
                  </span>
                </span>
                <input
                  type="number"
                  min="1"
                  max={factorOptions.length}
                  value={activeRules.thresholds?.mediumFactorsToMedium || 1}
                  disabled={disabled || !activeRules.thresholds?.mediumFactorsToMediumEnabled}
                  onChange={(e) => setThreshold("mediumFactorsToMedium", e.target.value)}
                />
              </label>
            ) : null}
            <label className="relationshipRiskThresholdLabel">
              <span className="relationshipRiskThresholdText">
                <input
                  className="form-check-input m-0 adminRiskSettingsCheckbox"
                  type="checkbox"
                  checked={!!activeRules.thresholds?.mediumFactorsToHighEnabled}
                  disabled={disabled}
                  onChange={(e) => setThresholdEnabled("mediumFactorsToHighEnabled", e.target.checked)}
                />
                <span className="relationshipRiskThresholdCopy">
                  <span className="relationshipRiskThresholdTitle">
                    {isTwoLevelModel ? "Risk factor escalation" : "Medium factor escalation"}
                  </span>
                  <span className="relationshipRiskThresholdSub">
                    {isTwoLevelModel
                      ? "Set High when risk factors and high risk factors are at least"
                      : "Set High when medium factors are at least"}
                  </span>
                </span>
              </span>
              <input
                type="number"
                min="1"
                max={factorOptions.length}
                value={activeRules.thresholds?.mediumFactorsToHigh || 1}
                disabled={disabled || !activeRules.thresholds?.mediumFactorsToHighEnabled}
                onChange={(e) => setThreshold("mediumFactorsToHigh", e.target.value)}
              />
            </label>
            <label className="relationshipRiskThresholdLabel">
              <span className="relationshipRiskThresholdText">
                <input
                  className="form-check-input m-0 adminRiskSettingsCheckbox"
                  type="checkbox"
                  checked={!!activeRules.thresholds?.highFactorsToHighEnabled}
                  disabled={disabled}
                  onChange={(e) => setThresholdEnabled("highFactorsToHighEnabled", e.target.checked)}
                />
                <span className="relationshipRiskThresholdCopy">
                  <span className="relationshipRiskThresholdTitle">High factor threshold</span>
                  <span className="relationshipRiskThresholdSub">Set High when high factors are at least</span>
                </span>
              </span>
              <input
                type="number"
                min="1"
                max={factorOptions.length}
                value={activeRules.thresholds?.highFactorsToHigh || 1}
                disabled={disabled || !activeRules.thresholds?.highFactorsToHighEnabled}
                onChange={(e) => setThreshold("highFactorsToHigh", e.target.value)}
              />
            </label>
            <label className="relationshipRiskThresholdLabel relationshipRiskThresholdLabel--combined">
              <span className="relationshipRiskThresholdText">
                <input
                  className="form-check-input m-0 adminRiskSettingsCheckbox"
                  type="checkbox"
                  checked={!!activeRules.thresholds?.combinedFactorsToHigh?.enabled}
                  disabled={disabled}
                  onChange={(e) => setCombinedThreshold("enabled", e.target.checked)}
                />
                <span className="relationshipRiskThresholdCopy">
                  <span className="relationshipRiskThresholdTitle">Combined factor escalation</span>
                  <span className="relationshipRiskThresholdSub">Set High when both thresholds are met</span>
                </span>
              </span>
              <span className="relationshipRiskThresholdPair">
                <span>
                  <span>High</span>
                  <input
                    type="number"
                    min="1"
                    max={factorOptions.length}
                    value={activeRules.thresholds?.combinedFactorsToHigh?.highFactors || 1}
                    disabled={disabled || !activeRules.thresholds?.combinedFactorsToHigh?.enabled}
                    onChange={(e) => setCombinedThreshold("highFactors", e.target.value)}
                  />
                </span>
                <span>
                  <span>{isTwoLevelModel ? "Risk" : "Medium"}</span>
                  <input
                    type="number"
                    min="1"
                    max={factorOptions.length}
                    value={activeRules.thresholds?.combinedFactorsToHigh?.mediumFactors || 1}
                    disabled={disabled || !activeRules.thresholds?.combinedFactorsToHigh?.enabled}
                    onChange={(e) => setCombinedThreshold("mediumFactors", e.target.value)}
                  />
                </span>
              </span>
            </label>
          </div>
        </div>

        <div className="d-flex align-items-center justify-content-between gap-2 mt-3 flex-wrap">
          {!isDefaultRiskSelection ? (
            <div className="riskDocDefaultPolicyToggle riskDocDefaultPolicyToggle--withAction">
              <label className="riskDocDefaultPolicyToggleLabel">
                <input
                  className="form-check-input m-0 adminRiskSettingsCheckbox"
                  type="checkbox"
                  checked={useDefaultRules}
                  onChange={(e) => setUseDefaultRules?.(e.target.checked)}
                />
                <span>Use default relationship risk rules</span>
              </label>
              {canPrefillDefault ? (
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={onPrefillDefault}
                  disabled={saving}
                >
                  Prefill default rules
                </button>
              ) : null}
            </div>
          ) : (
            <span className="small text-muted">
              Editing default relationship risk rules
            </span>
          )}
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <button
              type="button"
              className="btn btn-save1"
              onClick={onSave}
              disabled={saving || !canSave}
            >
              {saving ? "Saving…" : "Save relationship rules"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function uniqLines(arr) {
  const out = [];
  const seen = new Set();
  for (const raw of arr || []) {
    const v = String(raw || "").trim();
    if (!v) continue;
    const key = v.toLowerCase();
    if (!seen.has(key)) {
      seen.add(key);
      out.push(v);
    }
  }
  return out.sort((a, b) => a.localeCompare(b));
}

function parseLinesTextarea(text) {
  return uniqLines(
    String(text || "")
      .split(/[\n\r]+/g)
      .map((x) => x.trim())
      .filter(Boolean)
  );
}

const SOW_DOCUMENT_SOURCES = [
  {
    key: "employment",
    label: "Employment",
    docs: [
      { key: "payslips", label: "Payslips" },
      { key: "employment_contract", label: "Employment contract" },
      { key: "tax_return", label: "Tax return" },
      { key: "bank_statements", label: "Bank statement(s)", isBankStatement: true },
    ],
  },
  {
    key: "self_employed",
    label: "Self-employed",
    docs: [
      { key: "contracts", label: "Copies of contracts" },
      { key: "tax_return", label: "Tax return" },
      { key: "audited_fs", label: "Audited financial statement(s)" },
      { key: "bank_statements", label: "Bank statement(s)", isBankStatement: true },
    ],
  },
  {
    key: "business_owner",
    label: "Business owner",
    docs: [
      { key: "shareholder_register", label: "Shareholder register" },
      { key: "audited_fs", label: "Audited financial statement(s)" },
      { key: "corporate_doc", label: "Corporate document" },
      { key: "bank_statements", label: "Bank statement(s)", isBankStatement: true },
    ],
  },
  {
    key: "sale_business",
    label: "Sale of company",
    docs: [
      { key: "sale_agreement", label: "Sale agreement / SPA" },
      { key: "completion_statement", label: "Completion statement" },
      { key: "bank_statements", label: "Bank statement(s)", isBankStatement: true },
    ],
  },
  {
    key: "sale_real_estate",
    label: "Real estate sale",
    docs: [
      { key: "sale_agreement", label: "Sale agreement" },
      { key: "title_deed", label: "Title deed" },
      { key: "bank_statements", label: "Bank statement(s)", isBankStatement: true },
    ],
  },
  {
    key: "rental_income",
    label: "Rental income",
    docs: [
      { key: "rental_contract", label: "Rental contract" },
      { key: "title_deed", label: "Title deed" },
      { key: "bank_statements", label: "Bank statement(s)", isBankStatement: true },
    ],
  },
  {
    key: "investment_returns",
    label: "Investment returns",
    docs: [
      { key: "broker_statement", label: "Broker / portfolio statement" },
      { key: "trade_confirmation", label: "Trade confirmation(s)" },
      { key: "bank_statements", label: "Bank statement(s)", isBankStatement: true },
    ],
  },
  {
    key: "inheritance",
    label: "Inheritance",
    docs: [
      { key: "inheritance_certificate", label: "Certificate of inheritance" },
      { key: "grant_of_probate", label: "Grant of probate" },
      { key: "will_copy", label: "Copy of will" },
      { key: "bank_statements", label: "Bank statement(s)", isBankStatement: true },
    ],
  },
  {
    key: "gift",
    label: "Gift",
    docs: [
      { key: "gift_deed", label: "Gift deed" },
      { key: "donor_explanation", label: "Donor letter / explanation" },
      { key: "bank_statements", label: "Bank statement(s)", isBankStatement: true },
    ],
  },
  {
    key: "legal_settlement",
    label: "Legal settlement",
    docs: [
      { key: "court_order", label: "Court order / settlement agreement" },
      { key: "bank_statements", label: "Bank statement(s)", isBankStatement: true },
    ],
  },
  {
    key: "divorce_settlement",
    label: "Divorce settlement",
    docs: [
      { key: "divorce_decree", label: "Divorce decree / court order" },
      { key: "financial_settlement", label: "Financial settlement agreement" },
      { key: "bank_statements", label: "Bank statement(s)", isBankStatement: true },
    ],
  },
  {
    key: "virtual_asset_gains",
    label: "Virtual asset gains",
    docs: [
      { key: "exchange_statement", label: "Exchange statement" },
      { key: "trade_history", label: "Trade history / transaction report" },
      { key: "bank_statements", label: "Bank statement(s)", isBankStatement: true },
    ],
  },
  {
    key: "loan",
    label: "Loan",
    docs: [
      { key: "loan_agreement", label: "Loan agreement" },
      { key: "repayment_schedule", label: "Repayment schedule" },
      { key: "bank_statements", label: "Bank statement(s)", isBankStatement: true },
    ],
  },
  { key: "other", label: "Other", docs: [] },
];

function makeDocRef(doc) {
  if (typeof doc === "string") return { key: doc, label: "" };
  return { key: String(doc?.key || "").trim(), label: String(doc?.label || "").trim() };
}

function docKey(ref) {
  return makeDocRef(ref).key;
}

function slugDocKey(value) {
  const base = String(value || "")
    .trim()
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 48);
  return base || `document_${Date.now()}`;
}

function buildSupportingDocPolicySeedForModel(model, empty = false) {
  const riskOptions = supportingDocRisksForModel(model);
  const policy = {};

  SOW_DOCUMENT_SOURCES.forEach((source) => {
    const primaryDocs = source.docs.filter((doc) => !doc.isBankStatement);
    const bankDoc = source.docs.find((doc) => doc.isBankStatement);
    const group = primaryDocs.map((doc) => ({ key: doc.key, label: doc.label }));
    const defaultRule = empty
      ? { required: [], optional: [], groups: [] }
      : {
          required: [],
          optional: bankDoc ? [{ key: bankDoc.key, label: bankDoc.label }] : [],
          groups: group.length
            ? [{ id: `${source.key}_evidence`, label: "Required group", minRequired: 1, documents: group }]
            : [],
        };

    policy[source.key] = {};
    riskOptions.forEach((risk) => {
      policy[source.key][risk.key] = {
        required: [...defaultRule.required],
        optional: [...defaultRule.optional],
        groups: (defaultRule.groups || []).map((item) => ({
          ...item,
          documents: [...(item.documents || [])],
        })),
      };
    });
  });

  return policy;
}

function buildSupportingDocPolicySeed() {
  return {
    [RELATIONSHIP_RISK_MODEL_3_LEVEL]: buildSupportingDocPolicySeedForModel(RELATIONSHIP_RISK_MODEL_3_LEVEL),
    [RELATIONSHIP_RISK_MODEL_2_LEVEL]: buildSupportingDocPolicySeedForModel(RELATIONSHIP_RISK_MODEL_2_LEVEL),
  };
}

function buildEmptySupportingDocPolicySeed() {
  return {
    [RELATIONSHIP_RISK_MODEL_3_LEVEL]: buildSupportingDocPolicySeedForModel(RELATIONSHIP_RISK_MODEL_3_LEVEL, true),
    [RELATIONSHIP_RISK_MODEL_2_LEVEL]: buildSupportingDocPolicySeedForModel(RELATIONSHIP_RISK_MODEL_2_LEVEL, true),
  };
}

function sourceByKey(key) {
  return SOW_DOCUMENT_SOURCES.find((source) => source.key === key) || SOW_DOCUMENT_SOURCES[0];
}

function labelForDoc(sourceKey, ref) {
  const normalized = makeDocRef(ref);
  if (normalized.label) return normalized.label;
  const source = sourceByKey(sourceKey);
  return source.docs.find((doc) => doc.key === normalized.key)?.label || normalized.key;
}

function riskPolicy(policy, sourceKey, riskKey, model = RELATIONSHIP_RISK_MODEL_3_LEVEL) {
  const cleanModel = normalizeRelationshipRiskModel(model);
  return policy?.[cleanModel]?.[sourceKey]?.[riskKey] || { required: [], optional: [], groups: [] };
}

function policySummary(policy, sourceKey, riskKey, model = RELATIONSHIP_RISK_MODEL_3_LEVEL) {
  const cfg = riskPolicy(policy, sourceKey, riskKey, model);
  const required = [...(cfg.required || [])];
  const groups = [];

  (cfg.groups || []).forEach((group) => {
    const documents = group.documents || [];
    if (documents.length === 1) {
      required.push(documents[0]);
      return;
    }
    if (documents.length > 1) groups.push(group);
  });

  return {
    required,
    optional: cfg.optional || [],
    groups,
  };
}

function effectiveGroupMin(group) {
  const count = (group?.documents || []).length;
  const max = count > 2 ? count - 1 : 1;
  return Math.min(Math.max(1, Number(group?.minRequired) || 1), max);
}

function removeDocFromRiskConfig(cfg, section, docRef, groupId) {
  const key = docKey(docRef);
  if (section === "required" || section === "optional") {
    return {
      ...cfg,
      [section]: (cfg[section] || []).filter((doc) => docKey(doc) !== key),
    };
  }

  return {
    ...cfg,
    groups: (cfg.groups || [])
      .map((group) =>
        !groupId || group.id === groupId
          ? {
              ...group,
              documents: (group.documents || []).filter((doc) => docKey(doc) !== key),
            }
          : group
      )
      .filter((group) => (group.documents || []).length > 0),
  };
}

function SupportingDocBadge({ tone, children, onRemove }) {
  return (
    <span className={`riskDocBadge riskDocBadge--${tone}`}>
      <span>{children}</span>
      {onRemove ? (
        <button type="button" className="riskDocBadgeRemove" onClick={onRemove} aria-label="Remove document">
          x
        </button>
      ) : null}
    </span>
  );
}

function SupportingDocCell({ policy, sourceKey, riskKey, model }) {
  const summary = policySummary(policy, sourceKey, riskKey, model);
  const hasDocs =
    (summary.required || []).length ||
    (summary.optional || []).length ||
    (summary.groups || []).some((group) => (group.documents || []).length);

  return (
    <div className="riskDocCell">
      {!hasDocs ? <span className="riskDocEmpty">No preset docs</span> : null}
      {(summary.required || []).map((doc) => (
        <SupportingDocBadge key={`req-${docKey(doc)}`} tone="required">
          {labelForDoc(sourceKey, doc)}
        </SupportingDocBadge>
      ))}
      {(summary.groups || []).map((group) => (
        <div key={group.id} className="riskDocCellGroup">
          <div className="riskDocCellGroupLabel">{`At least ${effectiveGroupMin(group)} of`}</div>
          <div className="riskDocCellGroupBadges">
            {(group.documents || []).map((doc) => (
              <SupportingDocBadge key={`grp-${group.id}-${docKey(doc)}`} tone="group">
                {labelForDoc(sourceKey, doc)}
              </SupportingDocBadge>
            ))}
          </div>
        </div>
      ))}
      {(summary.optional || []).map((doc) => (
        <SupportingDocBadge key={`opt-${docKey(doc)}`} tone="optional">
          {labelForDoc(sourceKey, doc)}
        </SupportingDocBadge>
      ))}
    </div>
  );
}

function SupportingDocGroupEditor({
  sourceKey,
  group,
  onRemoveDoc,
  onChangeMinRequired,
}) {
  const documentCount = (group.documents || []).length;
  const currentMin = effectiveGroupMin(group);
  const showMinInput = documentCount > 2;

  return (
    <div className="riskDocGroupBox">
      <div className="riskDocGroupHead">
        <span>{`Required: at least ${currentMin} of`}</span>
        {showMinInput ? (
          <label>
            Min.
            <input
              type="number"
              min="1"
              max={Math.max(1, documentCount - 1)}
              value={currentMin}
              disabled={!onChangeMinRequired}
              onChange={(e) => onChangeMinRequired(Number(e.target.value) || 1)}
            />
          </label>
        ) : null}
      </div>
      <div className="riskDocBadgeWrap">
        {(group.documents || []).map((doc) => (
          <SupportingDocBadge
            key={docKey(doc)}
            tone="group"
            onRemove={onRemoveDoc ? () => onRemoveDoc("group", doc, group.id) : null}
          >
            {labelForDoc(sourceKey, doc)}
          </SupportingDocBadge>
        ))}
      </div>
    </div>
  );
}

function SupportingDocumentsPolicyCard({
  isDefaultRiskSelection = false,
  selectedOrgId = "",
  policy,
  setPolicy,
  relationshipRiskModel = RELATIONSHIP_RISK_MODEL_3_LEVEL,
  uboDocumentScope = DEFAULT_UBO_DOCUMENT_SCOPE,
  setUboDocumentScope,
  useDefaultPolicy = false,
  setUseDefaultPolicy,
  canPrefillDefault = false,
  onPrefillDefault,
  onSave,
  saving = false,
  canSave = true,
}) {
  const [selectedSourceKey, setSelectedSourceKey] = useState("employment");
  const [policyOverviewOpen, setPolicyOverviewOpen] = useState(false);
  const [policyEditorOpen, setPolicyEditorOpen] = useState(false);
  const [drafts, setDrafts] = useState({});

  const selectedSource = sourceByKey(selectedSourceKey);
  const activePolicyModel = normalizeRelationshipRiskModel(relationshipRiskModel);
  const activeDocRisks = supportingDocRisksForModel(activePolicyModel);

  useEffect(() => {
    if (isDefaultRiskSelection) setUseDefaultPolicy?.(false);
  }, [isDefaultRiskSelection, setUseDefaultPolicy]);

  function updateRiskConfig(sourceKeyToUpdate, riskKeyToUpdate, updater) {
    setPolicy((prev) => {
      const current = riskPolicy(prev, sourceKeyToUpdate, riskKeyToUpdate, activePolicyModel);
      return {
        ...prev,
        [activePolicyModel]: {
          ...(prev?.[activePolicyModel] || {}),
          [sourceKeyToUpdate]: {
            ...(prev?.[activePolicyModel]?.[sourceKeyToUpdate] || {}),
            [riskKeyToUpdate]: updater(current),
          },
        },
      };
    });
  }

  function setDraft(riskKey, patch) {
    const key = `${selectedSourceKey}:${riskKey}`;
    setDrafts((prev) => ({
      ...prev,
      [key]: {
        documentName: "",
        requirement: "required",
        requiredGroup: false,
        ...(prev[key] || {}),
        ...patch,
      },
    }));
  }

  function getDraft(riskKey) {
    return (
      drafts[`${selectedSourceKey}:${riskKey}`] || {
        documentName: "",
        requirement: "required",
        requiredGroup: false,
      }
    );
  }

  function addDraftDoc(riskKey) {
    const draft = getDraft(riskKey);
    const source = sourceByKey(selectedSourceKey);
    const documentName = String(draft.documentName || "").trim();
    const existingDoc = (source.docs || []).find(
      (doc) => doc.label.toLowerCase() === documentName.toLowerCase()
    );

    const doc = existingDoc
      ? { key: existingDoc.key, label: existingDoc.label }
      : documentName
      ? { key: `custom_${slugDocKey(documentName)}`, label: documentName }
      : null;

    if (!doc) return;

    updateRiskConfig(selectedSourceKey, riskKey, (cfg) => {
      const cleanCfg = removeDocFromRiskConfig(
        removeDocFromRiskConfig(removeDocFromRiskConfig(cfg, "required", doc), "optional", doc),
        "group",
        doc
      );

      if (draft.requirement === "optional") {
        return { ...cleanCfg, optional: [...(cleanCfg.optional || []), doc] };
      }

      if (draft.requiredGroup) {
        const groupId = `${selectedSourceKey}_${riskKey}_required_group`;
        const existingGroups = cleanCfg.groups || [];
        const foundGroup = existingGroups[0] || null;
        const nextGroup = foundGroup
          ? {
              ...foundGroup,
              minRequired: foundGroup.minRequired || 1,
              documents: [...(foundGroup.documents || []), doc],
            }
          : {
              id: groupId,
              label: "Required group",
              minRequired: 1,
              documents: [doc],
            };

        return {
          ...cleanCfg,
          groups: [nextGroup],
        };
      }

      return { ...cleanCfg, required: [...(cleanCfg.required || []), doc] };
    });

    setDraft(riskKey, { documentName: "" });
  }

  function removeDoc(riskKey, section, doc, groupId) {
    updateRiskConfig(selectedSourceKey, riskKey, (cfg) =>
      removeDocFromRiskConfig(cfg, section, doc, groupId)
    );
  }

  function changeGroupMin(riskKey, groupId, value) {
    updateRiskConfig(selectedSourceKey, riskKey, (cfg) => ({
      ...cfg,
      groups: (cfg.groups || []).map((group) =>
        group.id === groupId
          ? {
              ...group,
              minRequired: Math.min(
                Math.max(1, value || 1),
                Math.max(1, (group.documents || []).length - 1)
              ),
            }
          : group
      ),
    }));
  }

  return (
    <div className="card shadow-sm mb-3 riskDocPolicyCard">
      <div className="p-3 border-bottom">
        <div>
          <div className="fw-bold" style={{ color: "#4C5465" }}>
            Supporting Documents Policy
          </div>
          <div className="small text-muted">
            Configure document badges by relationship risk. Stored in{" "}
            <code>
              {isDefaultRiskSelection
                ? `${SUPPORTING_DOCUMENT_POLICY_COLLECTION}/lists`
                : `${SUPPORTING_DOCUMENT_POLICY_COLLECTION}/${selectedOrgId}`}
            </code>
            {!isDefaultRiskSelection &&
              `. If the default policy is enabled, document selection uses ${SUPPORTING_DOCUMENT_POLICY_COLLECTION}/lists.`}
          </div>
        </div>
      </div>

      <div className="p-3">
        <div className="mb-3">
          <div className="uboDocumentScopeCard">
            <div className="relationshipRiskModelToggleHeading countryUboScopeHeading uboDocumentScopeHeading">
              UBO document requirement scope
            </div>
            <div
              className="countryCombinationCards countryUboScopeCards uboDocumentScopeCards"
              role="radiogroup"
              aria-label="UBO document requirement scope"
            >
              {[
                {
                  value: "threshold_only",
                  label: "UBOs meeting thresholds",
                  detail: "API-qualified UBOs only",
                },
                {
                  value: "threshold_and_direct",
                  label: "UBOs meeting thresholds + Direct shareholder",
                  detail: "Add direct shareholders",
                },
                {
                  value: "all_ubos",
                  label: "All UBOs",
                  detail: "All individual UBOs",
                },
              ].map((option) => (
                <label
                  key={option.value}
                  className={`countryCombinationCard ${
                    uboDocumentScope === option.value ? "countryCombinationCard--active" : ""
                  }${saving || useDefaultPolicy ? " countryCombinationCard--disabled" : ""}`}
                >
                  <input
                    type="radio"
                    name="ubo-document-requirement-scope"
                    checked={uboDocumentScope === option.value}
                    disabled={saving || useDefaultPolicy}
                    onChange={() => setUboDocumentScope?.(option.value)}
                    style={{ display: "none" }}
                  />
                  <span className="countryCombinationCardTitle">
                    {option.label}
                  </span>
                  <span className="countryCombinationCardSub">{option.detail}</span>
                </label>
              ))}
            </div>
          </div>
        </div>

        <div className="riskDocExpandableSection">
          <button
            type="button"
            className={`riskExpandableHeader riskDocExpandableHeader${
              policyOverviewOpen ? " riskExpandableHeader--open" : ""
            }`}
            aria-expanded={policyOverviewOpen}
            onClick={() => setPolicyOverviewOpen((open) => !open)}
          >
            <div>
              <div className="fw-semibold" style={{ color: "#4C5465", fontSize: 14 }}>
                Policy overview
              </div>
              <div className="small text-muted">
                Overall view of document requirements by source of wealth.
              </div>
            </div>
            <RiskExpandableIcon open={policyOverviewOpen} />
          </button>

          <AnimatePresence initial={false}>
            {policyOverviewOpen && (
              <AnimatedCollapse>
                <div className="riskDocExpandableBody">
                  <div className="riskDocTableLegend">
                    <div className="riskDocLegend">
                      <span><i className="riskDocLegendDot riskDocLegendDot--required" />Required</span>
                      <span><i className="riskDocLegendDot riskDocLegendDot--optional" />Optional</span>
                    </div>
                  </div>

                  <div className="riskDocMatrixWrap">
                    <table className="riskDocMatrix">
                      <thead>
                        <tr>
                          <th>Source of wealth</th>
                          {activeDocRisks.map((risk) => (
                            <th key={risk.key}>{risk.label}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {SOW_DOCUMENT_SOURCES.map((source) => (
                          <tr
                            key={source.key}
                            className={source.key === selectedSourceKey ? "is-selected" : ""}
                            onClick={() => setSelectedSourceKey(source.key)}
                          >
                            <td>
                              <button type="button" className="riskDocSourceButton">
                                {source.label}
                              </button>
                            </td>
                            {activeDocRisks.map((risk) => (
                              <td key={risk.key}>
                                <SupportingDocCell
                                  policy={policy}
                                  sourceKey={source.key}
                                  riskKey={risk.key}
                                  model={activePolicyModel}
                                />
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </AnimatedCollapse>
            )}
          </AnimatePresence>
        </div>

        <div className="riskDocExpandableSection mt-3">
          <button
            type="button"
            className={`riskExpandableHeader riskDocExpandableHeader${
              policyEditorOpen ? " riskExpandableHeader--open" : ""
            }`}
            aria-expanded={policyEditorOpen}
            onClick={() => setPolicyEditorOpen((open) => !open)}
          >
            <div>
              <div className="fw-semibold" style={{ color: "#4C5465", fontSize: 14 }}>
                Policy editor
              </div>
              <div className="small text-muted">
                {selectedSource.label}
              </div>
            </div>
            <RiskExpandableIcon open={policyEditorOpen} />
          </button>

          <AnimatePresence initial={false}>
            {policyEditorOpen && (
              <AnimatedCollapse>
                <div className="riskDocExpandableBody">
              <label className="riskDocSourceSelect">
                <span>Source of wealth</span>
                <select
                  className="form-select form-select-sm"
                  value={selectedSourceKey}
                  onChange={(e) => setSelectedSourceKey(e.target.value)}
                >
                  {SOW_DOCUMENT_SOURCES.map((source) => (
                    <option key={source.key} value={source.key}>
                      {source.label}
                    </option>
                  ))}
                </select>
              </label>

              <div
                className={`riskDocEditorGrid ${
                  activeDocRisks.length === 2 ? "riskDocEditorGrid--two" : ""
                }`}
              >
                {activeDocRisks.map((risk) => {
                  const cfg = riskPolicy(policy, selectedSourceKey, risk.key, activePolicyModel);
                  const draft = getDraft(risk.key);
                  const alreadyUsedInRisk = new Set([
                    ...(cfg.required || []).map((doc) => docKey(doc)),
                    ...(cfg.optional || []).map((doc) => docKey(doc)),
                    ...(cfg.groups || []).flatMap((group) =>
                      (group.documents || []).map((doc) => docKey(doc))
                    ),
                  ]);
                  const existingOptions = (selectedSource.docs || []).filter(
                    (doc) => !alreadyUsedInRisk.has(doc.key)
                  );
                  const groupedRequiredMin = cfg.groups?.[0] ? effectiveGroupMin(cfg.groups[0]) : 1;

                  return (
                    <div key={risk.key} className={`riskDocRiskPanel riskDocRiskPanel--${risk.key}`}>
                      <div className="riskDocRiskTitle">{risk.label}</div>

                      <div className="riskDocRuleBucket riskDocRuleBucket--required">
                        <div className="riskDocRuleBucketHead">
                          <span>Required</span>
                        </div>
                        <div className="riskDocBadgeWrap">
                          {(cfg.required || []).length || (cfg.groups || []).length ? (
                            <>
                              {(cfg.required || []).map((doc) => (
                                <SupportingDocBadge
                                    key={docKey(doc)}
                                    tone="required"
                                  onRemove={useDefaultPolicy ? null : () => removeDoc(risk.key, "required", doc)}
                                >
                                  {labelForDoc(selectedSourceKey, doc)}
                                </SupportingDocBadge>
                              ))}
                              {(cfg.groups || []).map((group) =>
                                (group.documents || []).length === 1 ? (
                                  <SupportingDocBadge
                                    key={group.id}
                                    tone="required"
                                    onRemove={useDefaultPolicy ? null : () => removeDoc(risk.key, "group", group.documents[0], group.id)}
                                  >
                                    {labelForDoc(selectedSourceKey, group.documents[0])}
                                  </SupportingDocBadge>
                                ) : (
                                  <SupportingDocGroupEditor
                                    key={group.id}
                                    sourceKey={selectedSourceKey}
                                    group={group}
                                    onRemoveDoc={
                                      useDefaultPolicy
                                        ? null
                                        : (section, doc, groupId) => removeDoc(risk.key, section, doc, groupId)
                                    }
                                    onChangeMinRequired={
                                      useDefaultPolicy ? null : (value) => changeGroupMin(risk.key, group.id, value)
                                    }
                                  />
                                )
                              )}
                            </>
                          ) : (
                            <span className="riskDocEmpty">None</span>
                          )}
                        </div>
                      </div>

                      <div className="riskDocRuleBucket riskDocRuleBucket--optional">
                        <div className="riskDocRuleBucketHead">
                          <span>Optional</span>
                        </div>
                        <div className="riskDocBadgeWrap">
                          {(cfg.optional || []).length ? (
                            (cfg.optional || []).map((doc) => (
                              <SupportingDocBadge
                                key={docKey(doc)}
                                tone="optional"
                                onRemove={useDefaultPolicy ? null : () => removeDoc(risk.key, "optional", doc)}
                              >
                                {labelForDoc(selectedSourceKey, doc)}
                              </SupportingDocBadge>
                            ))
                          ) : (
                            <span className="riskDocEmpty">None</span>
                          )}
                        </div>
                      </div>

                      <div className="riskDocAddBox">
                        <input
                          className="form-control form-control-sm"
                          list={`risk-doc-options-${selectedSourceKey}-${risk.key}`}
                          value={draft.documentName}
                          disabled={useDefaultPolicy}
                          onChange={(e) => setDraft(risk.key, { documentName: e.target.value })}
                          placeholder="Select or type document type"
                        />
                        <datalist id={`risk-doc-options-${selectedSourceKey}-${risk.key}`}>
                          {existingOptions.map((doc) => (
                            <option key={doc.key} value={doc.label} />
                          ))}
                        </datalist>
                        <div className="riskDocRequirementRadios" role="radiogroup" aria-label="Document requirement">
                          <div className={`riskDocRequirementRadio riskDocRequirementRadio--stacked ${draft.requirement === "required" ? "is-checked" : ""}`}>
                            <label className="riskDocRequirementRadioMain">
                              <input
                                type="radio"
                                name={`risk-doc-requirement-${selectedSourceKey}-${risk.key}`}
                                value="required"
                                checked={draft.requirement === "required"}
                                disabled={useDefaultPolicy}
                                onChange={() =>
                                  setDraft(risk.key, {
                                    requirement: "required",
                                  })
                                }
                              />
                              <span>Required</span>
                            </label>
                            <label className="riskDocRequirementCheckbox">
                              <input
                                type="checkbox"
                                checked={!!draft.requiredGroup}
                                disabled={useDefaultPolicy || draft.requirement !== "required"}
                                onChange={(e) => setDraft(risk.key, { requiredGroup: e.target.checked })}
                              />
                              <span>{`At least ${groupedRequiredMin} of`}</span>
                            </label>
                          </div>
                          <label className="riskDocRequirementRadio">
                            <input
                              type="radio"
                              name={`risk-doc-requirement-${selectedSourceKey}-${risk.key}`}
                              value="optional"
                              checked={draft.requirement === "optional"}
                              disabled={useDefaultPolicy}
                              onChange={() =>
                                setDraft(risk.key, {
                                  requirement: "optional",
                                  requiredGroup: false,
                                })
                              }
                            />
                            <span>Optional</span>
                          </label>
                        </div>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary"
                          onClick={() => addDraftDoc(risk.key)}
                          disabled={useDefaultPolicy}
                        >
                          <i className="bi bi-plus-lg me-1" />
                          Add
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
                </div>
              </AnimatedCollapse>
            )}
          </AnimatePresence>
        </div>

        <div className="d-flex align-items-center justify-content-between gap-2 mt-3 flex-wrap">
          {!isDefaultRiskSelection ? (
            <div className="riskDocDefaultPolicyToggle riskDocDefaultPolicyToggle--withAction">
              <label className="riskDocDefaultPolicyToggleLabel">
                <input
                  className="form-check-input m-0 adminRiskSettingsCheckbox"
                  type="checkbox"
                  checked={useDefaultPolicy}
                  onChange={(e) => setUseDefaultPolicy?.(e.target.checked)}
                />
                <span>Use default supporting document policy</span>
              </label>
              {canPrefillDefault ? (
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={onPrefillDefault}
                  disabled={saving}
                >
                  Prefill default policy
                </button>
              ) : null}
            </div>
          ) : (
            <span className="small text-muted">
              Editing default supporting document policy
            </span>
          )}
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <button
              type="button"
              className="btn btn-save1"
              onClick={onSave}
              disabled={saving || !canSave}
            >
              {saving ? "Saving…" : "Save document policy"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ******************* EXPORT DEFAULT FUNCTION ********************

export default function AdminRiskSettingsPage() {
  const nav = useNavigate();
  const toastRef = useRef(null);

  const { me, role, isAdmin } = useAuth();
  const isManager = role === "manager";
  const myOrgId = String(me?.orgId || "").trim();

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  // Which org's settings are being edited
  const [selectedOrgId, setSelectedOrgId] = useState("");
  const [selectedOrgOption, setSelectedOrgOption] = useState(null);
  const [managerOrgName, setManagerOrgName] = useState("");
  // All orgs — for admin dropdown
  const [allOrgs, setAllOrgs] = useState([]);

  // ---- useState Countries ------
  const [fatfGreyText, setFatfGreyText] = useState("");
  const [fatfBlackText, setFatfBlackText] = useState("");
  const [ofacProgramsText, setOfacProgramsText] = useState("");
  const [taxHavensText, setTaxHavensText] = useState("");
  const [euHighRiskText, setEuHighRiskText] = useState("");
  const [customStdText, setCustomStdText] = useState("");
  const [customMedText, setCustomMedText] = useState("");
  const [customHighText, setCustomHighText] = useState("");
  const [generalCountryStdText, setGeneralCountryStdText] = useState("");
  const [generalCountryMedText, setGeneralCountryMedText] = useState("");
  const [generalCountryHighText, setGeneralCountryHighText] = useState("");
  const [countryUseGeneralList, setCountryUseGeneralList] = useState(false);
  const [countryUseMainCountryOfOperation, setCountryUseMainCountryOfOperation] = useState(false);
  const [generalCountryUseMainCountryOfOperation, setGeneralCountryUseMainCountryOfOperation] = useState(false);
  const [countryAdvancedOpen, setCountryAdvancedOpen] = useState({});
  const [countryRiskRulesOpen, setCountryRiskRulesOpen] = useState(false);
  const [defaultGlobalListsOpen, setDefaultGlobalListsOpen] = useState(false);
  const [countryRiskRules, setCountryRiskRules] = useState(() =>
    sanitizeCountryRiskRules(DEFAULT_COUNTRY_RISK_RULES)
  );

  // ---- useState Relationship risk rules ------
  const [relationshipRiskRules, setRelationshipRiskRules] = useState(() =>
    sanitizeRelationshipRiskSettings(DEFAULT_RELATIONSHIP_RISK_SETTINGS)
  );
  const [generalRelationshipRiskRules, setGeneralRelationshipRiskRules] = useState(() =>
    sanitizeRelationshipRiskSettings(DEFAULT_RELATIONSHIP_RISK_SETTINGS)
  );
  const [relationshipRiskUseGeneralSettings, setRelationshipRiskUseGeneralSettings] = useState(false);
  const [relationshipRiskUseGeneralModel, setRelationshipRiskUseGeneralModel] = useState(false);
  const [savingRelationshipRiskRules, setSavingRelationshipRiskRules] = useState(false);

  // ---- useState Industry ------
  const [indStdText, setIndStdText] = useState("");
  const [indMedText, setIndMedText] = useState("");
  const [indHighText, setIndHighText] = useState("");
  const [generalIndStdText, setGeneralIndStdText] = useState("");
  const [generalIndMedText, setGeneralIndMedText] = useState("");
  const [generalIndHighText, setGeneralIndHighText] = useState("");
  const [industryUseGeneralList, setIndustryUseGeneralList] = useState(false);
  const [savingIndustry, setSavingIndustry] = useState(false);

  // ---- useState Source of Wealth supporting documents ------
  const [supportingDocPolicy, setSupportingDocPolicy] = useState(() => buildSupportingDocPolicySeed());
  const [generalSupportingDocPolicy, setGeneralSupportingDocPolicy] = useState(() => buildSupportingDocPolicySeed());
  const [supportingDocUseGeneralList, setSupportingDocUseGeneralList] = useState(false);
  const [supportingDocUboDocumentScope, setSupportingDocUboDocumentScope] = useState(DEFAULT_UBO_DOCUMENT_SCOPE);
  const [generalSupportingDocUboDocumentScope, setGeneralSupportingDocUboDocumentScope] = useState(DEFAULT_UBO_DOCUMENT_SCOPE);
  const [savingSupportingDocs, setSavingSupportingDocs] = useState(false);

  const [savingLists, setSavingLists] = useState(false);
  const [savedSnapshots, setSavedSnapshots] = useState({
    country: "",
    industry: "",
    supportingDocs: "",
    relationshipRisk: "",
  });

  // ---- Access check ----
  async function assertAccessOrThrow() {
    const u = auth.currentUser;
    if (!u) throw new Error("You must be signed in.");
    const snap = await getDoc(doc(db, "users", u.uid));
    if (!snap.exists()) throw new Error("User profile missing.");
    const data = snap.data();
    if (data?.disabled) throw new Error("Your account is disabled.");
    const r = String(data?.role || "").toLowerCase();
    if (r !== "admin" && r !== "manager")
      throw new Error("Access denied: manager or admin only.");
  }

  // ---- Load all orgs (admin only) ----
  async function loadAllOrgs() {
    try {
      const snap = await getDocs(collection(db, "orgs"));
      const list = snap.docs
        .map((d) => ({ orgId: d.id, name: d.data().name || d.id, disabled: !!d.data().disabled }))
        .filter((o) => !o.disabled)
        .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
      setAllOrgs(list);
    } catch (e) {
      setErr("Failed to load organisations: " + (e?.message || ""));
    }
  }

  async function loadManagerOrgName(orgId) {
    const cleanOrgId = String(orgId || "").trim();
    if (!cleanOrgId) return;
    try {
      const snap = await getDoc(doc(db, "orgs", cleanOrgId));
      if (!snap.exists()) {
        setManagerOrgName(cleanOrgId);
        return;
      }
      const data = snap.data();
      setManagerOrgName(String(data?.name || data?.displayName || cleanOrgId));
    } catch (e) {
      setManagerOrgName(cleanOrgId);
    }
  }

  // ---- Initialise org selection ----
  useEffect(() => {
    // Both staff categories stay excluded here, exactly as legacy staff were.
    if (!role || isStaffCategory(role)) return;

    if (isManager) {
      if (myOrgId) {
        setSelectedOrgId(myOrgId);
        loadManagerOrgName(myOrgId);
      }
      else {
        setErr("No organisation assigned to your account.");
        setLoading(false);
      }
      return;
    }

    if (isAdmin) {
      setSelectedOrgId(DEFAULT_RISK_OPTION_ID);
      setSelectedOrgOption({ value: DEFAULT_RISK_OPTION_ID, label: "Default" });
      loadAllOrgs();
    }
    // eslint-disable-next-line
  }, [isAdmin, isManager, myOrgId, role]);

  // ---- Load lists when selectedOrgId is ready ----
  useEffect(() => {
    if (selectedOrgId) loadLists();
    // eslint-disable-next-line
  }, [selectedOrgId]);

  async function handleLogout() {
    sessionStorage.setItem("mykyc:loggedOut", "1");
    await signOut(auth);
    nav("/login", { replace: true });
  }

  // ---- Function loadLists (LOAD) ------
  async function loadLists(targetOrgId = selectedOrgId) {
    const orgIdToLoad = String(targetOrgId || "").trim();
    if (!orgIdToLoad) return;
    const isDefaultSelection = orgIdToLoad === DEFAULT_RISK_OPTION_ID;
    setErr("");
    setLoading(true);
    try {
      const readPromises = [
        assertAccessOrThrow(),
        getDoc(doc(db, "countryRisk", "lists")),
        getDoc(doc(db, "industryRisk", "lists")),
        getDoc(doc(db, SUPPORTING_DOCUMENT_POLICY_COLLECTION, "lists")),
        getDoc(doc(db, RELATIONSHIP_RISK_SETTINGS_COLLECTION, RELATIONSHIP_RISK_DEFAULT_DOC_ID)),
      ];

      if (!isDefaultSelection) {
        readPromises.push(
          getDoc(doc(db, "countryRisk", orgIdToLoad)),
          getDoc(doc(db, "industryRisk", orgIdToLoad)),
          getDoc(doc(db, SUPPORTING_DOCUMENT_POLICY_COLLECTION, orgIdToLoad)),
          getDoc(doc(db, RELATIONSHIP_RISK_SETTINGS_COLLECTION, orgIdToLoad))
        );
      }

      const [
        ,
        globalSnap,
        generalIndSnap,
        generalSupportingDocSnap,
        generalRelationshipRiskSnap,
        orgSnap,
        indSnap,
        supportingDocSnap,
        relationshipRiskSnap,
      ] = await Promise.all(readPromises);

      // Global lists (FATF/OFAC/taxHavens) — shared across all orgs
      const global = globalSnap.exists() ? globalSnap.data() : {};
      const globalFatfGrey = Array.isArray(global.fatfGrey) ? global.fatfGrey : [];
      const globalFatfBlack = Array.isArray(global.fatfBlack) ? global.fatfBlack : [];
      const globalOfacPrograms = Array.isArray(global.ofacPrograms) ? global.ofacPrograms : [];
      const globalTaxHavens = Array.isArray(global.taxHavens) ? global.taxHavens : [];
      const globalEuHighRisk = Array.isArray(global.euHighRisk) ? global.euHighRisk : [];
      const globalCountryRules = sanitizeCountryRiskRules(global.rules);
      const globalUseMainCountryOfOperation = global.useMainCountryOfOperation === true;
      const hasSavedDefaultCountryCategorisation =
        Array.isArray(global.customStandard) ||
        Array.isArray(global.customMedium) ||
        Array.isArray(global.customHigh);
      const derivedDefaultCountryCategorisation = hasSavedDefaultCountryCategorisation
        ? null
        : buildCountryCategorisationFromDefaultSources({
            fatfBlackList: globalFatfBlack,
            fatfGreyList: globalFatfGrey,
            ofacProgramsList: globalOfacPrograms,
            taxHavensList: globalTaxHavens,
            euHighRiskList: globalEuHighRisk,
            rules: globalCountryRules,
          });
      const defaultCountryStandard = hasSavedDefaultCountryCategorisation
        ? uniqIso3(global.customStandard || [])
        : derivedDefaultCountryCategorisation.standard;
      const defaultCountryMedium = hasSavedDefaultCountryCategorisation
        ? uniqIso3(global.customMedium || [])
        : derivedDefaultCountryCategorisation.medium;
      const defaultCountryHigh = hasSavedDefaultCountryCategorisation
        ? uniqIso3(global.customHigh || [])
        : derivedDefaultCountryCategorisation.high;

      setFatfGreyText(globalFatfGrey.join("\n"));
      setFatfBlackText(globalFatfBlack.join("\n"));
      setOfacProgramsText(globalOfacPrograms.join("\n"));
      setTaxHavensText(globalTaxHavens.join("\n"));
      setEuHighRiskText(globalEuHighRisk.join("\n"));
      setCountryRiskRules(globalCountryRules);
      setGeneralCountryStdText(defaultCountryStandard.join("\n"));
      setGeneralCountryMedText(defaultCountryMedium.join("\n"));
      setGeneralCountryHighText(defaultCountryHigh.join("\n"));
      setGeneralCountryUseMainCountryOfOperation(globalUseMainCountryOfOperation);

      const org = !isDefaultSelection && orgSnap?.exists() ? orgSnap.data() : {};

      if (isDefaultSelection) {
        setCustomStdText(defaultCountryStandard.join("\n"));
        setCustomMedText(defaultCountryMedium.join("\n"));
        setCustomHighText(defaultCountryHigh.join("\n"));
        setCountryUseGeneralList(false);
        setCountryUseMainCountryOfOperation(globalUseMainCountryOfOperation);
      } else {
        // Per-org custom lists
        setCustomStdText((org.customStandard || []).join("\n"));
        setCustomMedText((org.customMedium || []).join("\n"));
        setCustomHighText((org.customHigh || []).join("\n"));
        setCountryUseGeneralList(org.useGeneralList === true);
        setCountryUseMainCountryOfOperation(org.useMainCountryOfOperation === true);
      }

      // General industry lists
      const generalInd = generalIndSnap.exists() ? generalIndSnap.data() : {};
      setGeneralIndStdText((generalInd.standard || []).join("\n"));
      setGeneralIndMedText((generalInd.medium || []).join("\n"));
      setGeneralIndHighText((generalInd.high || []).join("\n"));

      const generalSupportingDocData = generalSupportingDocSnap.exists() ? generalSupportingDocSnap.data() : {};
      const generalSupportingPolicy = sanitizeSupportingDocumentPolicy(
        generalSupportingDocData.sow || generalSupportingDocData.policy || buildSupportingDocPolicySeed()
      );
      const generalUboDocumentScope = normalizeUboDocumentScope(
        generalSupportingDocData.uboDocumentScope
      );
      setGeneralSupportingDocPolicy(generalSupportingPolicy);
      setGeneralSupportingDocUboDocumentScope(generalUboDocumentScope);

      const generalRelationshipRiskData = generalRelationshipRiskSnap.exists()
        ? generalRelationshipRiskSnap.data()
        : {};
      const generalRelationshipRiskSettings = sanitizeRelationshipRiskSettings(
        generalRelationshipRiskData.rules || generalRelationshipRiskData
      );
      setGeneralRelationshipRiskRules(generalRelationshipRiskSettings);

      if (isDefaultSelection) {
        setIndStdText((generalInd.standard || []).join("\n"));
        setIndMedText((generalInd.medium || []).join("\n"));
        setIndHighText((generalInd.high || []).join("\n"));
        setIndustryUseGeneralList(false);
        setSupportingDocPolicy(generalSupportingPolicy);
        setSupportingDocUboDocumentScope(generalUboDocumentScope);
        setSupportingDocUseGeneralList(false);
        setRelationshipRiskRules(generalRelationshipRiskSettings);
        setRelationshipRiskUseGeneralSettings(false);
        setRelationshipRiskUseGeneralModel(false);
      } else {
        // Per-org industry lists
        const ind = indSnap.exists() ? indSnap.data() : {};
        setIndStdText((ind.standard || []).join("\n"));
        setIndMedText((ind.medium || []).join("\n"));
        setIndHighText((ind.high || []).join("\n"));
        setIndustryUseGeneralList(ind.useGeneralList === true);

        const supportingDocData = supportingDocSnap.exists() ? supportingDocSnap.data() : {};
        const orgUboDocumentScope = supportingDocData.uboDocumentScope
          ? normalizeUboDocumentScope(supportingDocData.uboDocumentScope)
          : generalUboDocumentScope;
        setSupportingDocUseGeneralList(supportingDocData.useGeneralList === true);
        setSupportingDocUboDocumentScope(orgUboDocumentScope);
        setSupportingDocPolicy(
          sanitizeSupportingDocumentPolicy(
            supportingDocData.sow || supportingDocData.policy || buildEmptySupportingDocPolicySeed()
          )
        );

        const relationshipRiskData = relationshipRiskSnap.exists() ? relationshipRiskSnap.data() : {};
        setRelationshipRiskUseGeneralSettings(relationshipRiskData.useGeneralSettings === true);
        setRelationshipRiskUseGeneralModel(relationshipRiskData.useGeneralModel === true);
        setRelationshipRiskRules(
          sanitizeRelationshipRiskSettings(relationshipRiskData.rules || relationshipRiskData)
        );
      }

      const ind = !isDefaultSelection && indSnap?.exists() ? indSnap.data() : {};
      const supportingDocData =
        !isDefaultSelection && supportingDocSnap?.exists() ? supportingDocSnap.data() : {};
      const relationshipRiskData =
        !isDefaultSelection && relationshipRiskSnap?.exists() ? relationshipRiskSnap.data() : {};

      setSavedSnapshots({
        country: stableStringify(
          isDefaultSelection
            ? {
                fatfGrey: uniqIso3(globalFatfGrey),
                fatfBlack: uniqIso3(globalFatfBlack),
                ofacPrograms: uniqIso3(globalOfacPrograms),
                taxHavens: uniqIso3(globalTaxHavens),
                euHighRisk: uniqIso3(globalEuHighRisk),
                customStandard: defaultCountryStandard,
                customMedium: defaultCountryMedium,
                customHigh: defaultCountryHigh,
                useMainCountryOfOperation: globalUseMainCountryOfOperation,
                rules: globalCountryRules,
              }
            : {
                customStandard: uniqIso3(org.customStandard || []),
                customMedium: uniqIso3(org.customMedium || []),
                customHigh: uniqIso3(org.customHigh || []),
                useGeneralList: org.useGeneralList === true,
                useMainCountryOfOperation: org.useMainCountryOfOperation === true,
              }
        ),
        industry: stableStringify(
          isDefaultSelection
            ? {
                standard: uniqLines(generalInd.standard || []),
                medium: uniqLines(generalInd.medium || []),
                high: uniqLines(generalInd.high || []),
              }
            : {
                useGeneralList: ind.useGeneralList === true,
                standard: uniqLines(ind.standard || []),
                medium: uniqLines(ind.medium || []),
                high: uniqLines(ind.high || []),
              }
        ),
        supportingDocs: stableStringify(
          isDefaultSelection
            ? {
                policy: generalSupportingPolicy,
                uboDocumentScope: generalUboDocumentScope,
              }
            : {
                useGeneralList: supportingDocData.useGeneralList === true,
                policy: sanitizeSupportingDocumentPolicy(
                  supportingDocData.sow ||
                    supportingDocData.policy ||
                    buildEmptySupportingDocPolicySeed()
                ),
                uboDocumentScope: supportingDocData.uboDocumentScope
                  ? normalizeUboDocumentScope(supportingDocData.uboDocumentScope)
                  : generalUboDocumentScope,
              }
        ),
        relationshipRisk: stableStringify(
          isDefaultSelection
            ? {
                rules: generalRelationshipRiskSettings,
              }
            : {
                useGeneralSettings: relationshipRiskData.useGeneralSettings === true,
                useGeneralModel: relationshipRiskData.useGeneralModel === true,
                rules: sanitizeRelationshipRiskSettings(
                  relationshipRiskData.rules || relationshipRiskData
                ),
              }
        ),
      });
    } catch (e) {
      setErr(e?.message || "Failed to load lists");
    } finally {
      setLoading(false);
    }
  }

  // ------ useMemo Countries -----
  const fatfGrey = useMemo(() => parseIso3Textarea(fatfGreyText), [fatfGreyText]);
  const fatfBlack = useMemo(() => parseIso3Textarea(fatfBlackText), [fatfBlackText]);
  const ofacPrograms = useMemo(() => parseIso3Textarea(ofacProgramsText), [ofacProgramsText]);
  const taxHavens = useMemo(() => parseIso3Textarea(taxHavensText), [taxHavensText]);
  const euHighRisk = useMemo(() => parseIso3Textarea(euHighRiskText), [euHighRiskText]);
  const customStandard = useMemo(() => parseIso3Textarea(customStdText), [customStdText]);
  const customMedium = useMemo(() => parseIso3Textarea(customMedText), [customMedText]);
  const customHigh = useMemo(() => parseIso3Textarea(customHighText), [customHighText]);
  const generalCountryStandard = useMemo(() => parseIso3Textarea(generalCountryStdText), [generalCountryStdText]);
  const generalCountryMedium = useMemo(() => parseIso3Textarea(generalCountryMedText), [generalCountryMedText]);
  const generalCountryHigh = useMemo(() => parseIso3Textarea(generalCountryHighText), [generalCountryHighText]);

  // ---- useMemo Industry ------
  const industryStandard = useMemo(() => parseLinesTextarea(indStdText), [indStdText]);
  const industryMedium = useMemo(() => parseLinesTextarea(indMedText), [indMedText]);
  const industryHigh = useMemo(() => parseLinesTextarea(indHighText), [indHighText]);
  const generalIndustryStandard = useMemo(() => parseLinesTextarea(generalIndStdText), [generalIndStdText]);
  const generalIndustryMedium = useMemo(() => parseLinesTextarea(generalIndMedText), [generalIndMedText]);
  const generalIndustryHigh = useMemo(() => parseLinesTextarea(generalIndHighText), [generalIndHighText]);
  const isDefaultRiskSelection = selectedOrgId === DEFAULT_RISK_OPTION_ID;
  const canEditGlobalCountryLists = isAdmin && isDefaultRiskSelection;
  const displayedCountryStdText = !isDefaultRiskSelection && countryUseGeneralList ? generalCountryStdText : customStdText;
  const displayedCountryMedText = !isDefaultRiskSelection && countryUseGeneralList ? generalCountryMedText : customMedText;
  const displayedCountryHighText = !isDefaultRiskSelection && countryUseGeneralList ? generalCountryHighText : customHighText;
  const displayedCountryStandard = !isDefaultRiskSelection && countryUseGeneralList ? generalCountryStandard : customStandard;
  const displayedCountryMedium = !isDefaultRiskSelection && countryUseGeneralList ? generalCountryMedium : customMedium;
  const displayedCountryHigh = !isDefaultRiskSelection && countryUseGeneralList ? generalCountryHigh : customHigh;
  const effectiveCustomStandard = !isDefaultRiskSelection && countryUseGeneralList ? generalCountryStandard : customStandard;
  const effectiveCustomMedium = !isDefaultRiskSelection && countryUseGeneralList ? generalCountryMedium : customMedium;
  const effectiveCustomHigh = !isDefaultRiskSelection && countryUseGeneralList ? generalCountryHigh : customHigh;
  const displayedIndStdText = !isDefaultRiskSelection && industryUseGeneralList ? generalIndStdText : indStdText;
  const displayedIndMedText = !isDefaultRiskSelection && industryUseGeneralList ? generalIndMedText : indMedText;
  const displayedIndHighText = !isDefaultRiskSelection && industryUseGeneralList ? generalIndHighText : indHighText;
  const displayedIndustryStandard = !isDefaultRiskSelection && industryUseGeneralList ? generalIndustryStandard : industryStandard;
  const displayedIndustryMedium = !isDefaultRiskSelection && industryUseGeneralList ? generalIndustryMedium : industryMedium;
  const displayedIndustryHigh = !isDefaultRiskSelection && industryUseGeneralList ? generalIndustryHigh : industryHigh;
  const displayedSupportingDocPolicy =
    !isDefaultRiskSelection && supportingDocUseGeneralList
      ? generalSupportingDocPolicy
      : supportingDocPolicy;
  const displayedSupportingDocUboDocumentScope =
    supportingDocUboDocumentScope;
  const displayedRelationshipRiskRules =
    !isDefaultRiskSelection && relationshipRiskUseGeneralSettings
      ? generalRelationshipRiskRules
      : relationshipRiskRules;
  const activeFinalRiskModel =
    !isDefaultRiskSelection &&
    (relationshipRiskUseGeneralSettings || relationshipRiskUseGeneralModel)
      ? sanitizeRelationshipRiskSettings(generalRelationshipRiskRules).activeModel
      : sanitizeRelationshipRiskSettings(displayedRelationshipRiskRules).activeModel;
  const isTwoLevelFinalRiskModel = activeFinalRiskModel === RELATIONSHIP_RISK_MODEL_2_LEVEL;

  const countrySnapshot = useMemo(
    () =>
      stableStringify(
        isDefaultRiskSelection
          ? {
              fatfGrey,
              fatfBlack,
              ofacPrograms,
              taxHavens,
              euHighRisk,
              customStandard,
              customMedium,
              customHigh,
              useMainCountryOfOperation: countryUseMainCountryOfOperation,
              rules: sanitizeCountryRiskRules(countryRiskRules),
            }
          : {
              customStandard,
              customMedium,
              customHigh,
              useGeneralList: countryUseGeneralList,
              useMainCountryOfOperation: countryUseMainCountryOfOperation,
            }
      ),
    [
      countryRiskRules,
      countryUseGeneralList,
      countryUseMainCountryOfOperation,
      customHigh,
      customMedium,
      customStandard,
      euHighRisk,
      fatfBlack,
      fatfGrey,
      isDefaultRiskSelection,
      ofacPrograms,
      taxHavens,
    ]
  );

  const industrySnapshot = useMemo(
    () =>
      stableStringify(
        isDefaultRiskSelection
          ? {
              standard: industryStandard,
              medium: industryMedium,
              high: industryHigh,
            }
          : {
              useGeneralList: industryUseGeneralList,
              standard: industryStandard,
              medium: industryMedium,
              high: industryHigh,
            }
      ),
    [
      industryHigh,
      industryMedium,
      industryStandard,
      industryUseGeneralList,
      isDefaultRiskSelection,
    ]
  );

  const supportingDocsSnapshot = useMemo(
    () =>
      stableStringify(
          isDefaultRiskSelection
            ? {
              policy: sanitizeSupportingDocumentPolicy(supportingDocPolicy),
              uboDocumentScope: normalizeUboDocumentScope(supportingDocUboDocumentScope),
            }
          : {
              useGeneralList: supportingDocUseGeneralList,
              policy: sanitizeSupportingDocumentPolicy(supportingDocPolicy),
              uboDocumentScope: normalizeUboDocumentScope(supportingDocUboDocumentScope),
            }
      ),
    [
      isDefaultRiskSelection,
      supportingDocPolicy,
      supportingDocUboDocumentScope,
      supportingDocUseGeneralList,
    ]
  );

  const relationshipRiskSnapshot = useMemo(
    () =>
      stableStringify(
        isDefaultRiskSelection
          ? {
              rules: sanitizeRelationshipRiskSettings(relationshipRiskRules),
            }
          : {
              useGeneralSettings: relationshipRiskUseGeneralSettings,
              useGeneralModel: relationshipRiskUseGeneralModel,
              rules: sanitizeRelationshipRiskSettings(relationshipRiskRules),
            }
      ),
    [
      isDefaultRiskSelection,
      relationshipRiskRules,
      relationshipRiskUseGeneralModel,
      relationshipRiskUseGeneralSettings,
    ]
  );

  const countryDirty = !!savedSnapshots.country && countrySnapshot !== savedSnapshots.country;
  const industryDirty = !!savedSnapshots.industry && industrySnapshot !== savedSnapshots.industry;
  const supportingDocsDirty =
    !!savedSnapshots.supportingDocs && supportingDocsSnapshot !== savedSnapshots.supportingDocs;
  const relationshipRiskDirty =
    !!savedSnapshots.relationshipRisk &&
    relationshipRiskSnapshot !== savedSnapshots.relationshipRisk;

  const countryOverrideRows = useMemo(() => {
    const defaultSourceMap = new Map();

    function addDefaultCountries(list, sourceKey, source) {
      for (const iso3 of list || []) {
        if (!iso3) continue;
        const existing = defaultSourceMap.get(iso3);
        if (!existing) {
          defaultSourceMap.set(iso3, { sourceKeys: [sourceKey], sources: [source] });
          continue;
        }
        if (!existing.sourceKeys.includes(sourceKey)) existing.sourceKeys.push(sourceKey);
        if (!existing.sources.includes(source)) existing.sources.push(source);
      }
    }

    addDefaultCountries(fatfBlack, "fatfBlack", "FATF Black list");
    addDefaultCountries(ofacPrograms, "ofacPrograms", "OFAC sanctions");
    addDefaultCountries(fatfGrey, "fatfGrey", "FATF Grey list");
    addDefaultCountries(taxHavens, "taxHavens", "EU Tax havens");
    addDefaultCountries(euHighRisk, "euHighRisk", "EU High-Risk Third Countries");

    const rows = [];

    function addCustomCountries(list, customRisk) {
      for (const iso3 of list || []) {
        const defaultSourceInfo = defaultSourceMap.get(iso3);
        const defaultRisk = countryRiskFromSourceKeys(countryRiskRules, defaultSourceInfo?.sourceKeys || []);
        const defaultInfo = defaultSourceInfo ? { ...defaultSourceInfo, risk: defaultRisk } : null;
        if (!defaultInfo || defaultInfo.risk === customRisk) continue;
        const direction = overrideDirection(defaultInfo.risk, customRisk);
        rows.push({
          iso3,
          country: nameFromIso3(iso3) || iso3,
          defaultRisk: defaultInfo.risk,
          customRisk,
          direction,
          sources: defaultInfo.sources,
        });
      }
    }

    addCustomCountries(effectiveCustomStandard, "standard");
    addCustomCountries(effectiveCustomMedium, "medium");
    addCustomCountries(effectiveCustomHigh, "high");

    return rows.sort((a, b) => {
      if (a.direction !== b.direction) return a.direction === "downgrade" ? -1 : 1;
      return a.country.localeCompare(b.country);
    });
  }, [countryRiskRules, effectiveCustomHigh, effectiveCustomMedium, effectiveCustomStandard, fatfBlack, fatfGrey, ofacPrograms, taxHavens, euHighRisk]);

  const countryOverrideMapByRisk = useMemo(() => {
    const empty = { standard: {}, medium: {}, high: {} };
    for (const row of countryOverrideRows) {
      empty[row.customRisk][row.iso3] = row;
    }
    return empty;
  }, [countryOverrideRows]);

  const countryOverrideMapByDefaultList = useMemo(() => {
    const empty = { fatfGrey: {}, fatfBlack: {}, ofacPrograms: {}, taxHavens: {}, euHighRisk: {} };
    const sourceToKey = {
      "FATF Grey list": "fatfGrey",
      "FATF Black list": "fatfBlack",
      "OFAC sanctions": "ofacPrograms",
      "EU Tax havens": "taxHavens",
      "EU High-Risk Third Countries": "euHighRisk",
    };

    for (const row of countryOverrideRows) {
      for (const source of row.sources || []) {
        const key = sourceToKey[source];
        if (key) empty[key][row.iso3] = row;
      }
    }

    return empty;
  }, [countryOverrideRows]);

  const countryOverrideDowngradeCount = useMemo(
    () => countryOverrideRows.filter((row) => row.direction === "downgrade").length,
    [countryOverrideRows]
  );

  const countryOptions = useMemo(() => {
    const map = countries.getNames("en") || {};
    return Object.entries(map)
      .map(([iso2, name]) => {
        const iso3 = countries.alpha2ToAlpha3(iso2);
        return iso3
          ? { value: iso3.toUpperCase(), iso3: iso3.toUpperCase(), label: name }
          : null;
      })
      .filter(Boolean)
      .sort((a, b) => a.label.localeCompare(b.label));
  }, []);

  const toggleCountryAdvanced = useCallback((key) => {
    setCountryAdvancedOpen((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  }, []);

  // ---- Function saveLists COUNTRY (SAVE) ------
  async function saveCountryLists() {
    if (savingLists || !selectedOrgId || !countryDirty) return;
    setErr("");
    setSavingLists(true);
    try {
      await assertAccessOrThrow();

      // Global country lists are editable only from the admin Default view.
      if (canEditGlobalCountryLists) {
        await setDoc(
          doc(db, "countryRisk", "lists"),
          {
            fatfGrey,
            fatfBlack,
            ofacPrograms,
            taxHavens,
            euHighRisk,
            customStandard,
            customMedium,
            customHigh,
            useMainCountryOfOperation: countryUseMainCountryOfOperation,
            rules: sanitizeCountryRiskRules(countryRiskRules),
            updatedAt: serverTimestamp(),
          },
          { merge: true }
        );
        setGeneralCountryStdText(customStdText);
        setGeneralCountryMedText(customMedText);
        setGeneralCountryHighText(customHighText);
        setGeneralCountryUseMainCountryOfOperation(countryUseMainCountryOfOperation);
      }

      // Both admin and manager save per-org custom lists
      if (!isDefaultRiskSelection) {
        await setDoc(
          doc(db, "countryRisk", selectedOrgId),
          {
            customStandard,
            customMedium,
            customHigh,
            useGeneralList: countryUseGeneralList,
            useMainCountryOfOperation: countryUseMainCountryOfOperation,
            updatedAt: serverTimestamp(),
          },
          { merge: true }
        );
      }

      setSavedSnapshots((prev) => ({ ...prev, country: countrySnapshot }));
      toastRef.current?.show({ severity: "success", summary: "Saved", detail: "Country lists updated.", life: 2500 });
    } catch (e) {
      toastRef.current?.show({ severity: "error", summary: "Save failed", detail: e?.message || "Failed to save lists", life: 4500 });
    } finally {
      setSavingLists(false);
    }
  }

  // ---- Function saveLists INDUSTRIES (SAVE) ------
  async function saveIndustryLists() {
    if (savingIndustry || !selectedOrgId || !industryDirty) return;
    setSavingIndustry(true);
    try {
      await assertAccessOrThrow();
      if (isDefaultRiskSelection) {
        await setDoc(
          doc(db, "industryRisk", "lists"),
          {
            standard: industryStandard,
            medium: industryMedium,
            high: industryHigh,
            updatedAt: serverTimestamp(),
          },
          { merge: true }
        );
        setGeneralIndStdText(indStdText);
        setGeneralIndMedText(indMedText);
        setGeneralIndHighText(indHighText);
      } else {
        await setDoc(
          doc(db, "industryRisk", selectedOrgId),
          {
            useGeneralList: industryUseGeneralList,
            standard: industryStandard,
            medium: industryMedium,
            high: industryHigh,
            updatedAt: serverTimestamp(),
          },
          { merge: true }
        );
      }
      setSavedSnapshots((prev) => ({ ...prev, industry: industrySnapshot }));
      toastRef.current?.show({ severity: "success", summary: "Saved", detail: "Industry lists updated.", life: 2500 });
    } catch (e) {
      toastRef.current?.show({ severity: "error", summary: "Save failed", detail: e?.message || "Failed to save industry lists", life: 4500 });
    } finally {
      setSavingIndustry(false);
    }
  }

  function prefillIndustryListsFromDefault() {
    setIndStdText(generalIndStdText);
    setIndMedText(generalIndMedText);
    setIndHighText(generalIndHighText);
    setIndustryUseGeneralList(false);
  }

  function buildCountryCategorisationFromDefaultSources({
    fatfBlackList = fatfBlack,
    fatfGreyList = fatfGrey,
    ofacProgramsList = ofacPrograms,
    taxHavensList = taxHavens,
    euHighRiskList = euHighRisk,
    rules = countryRiskRules,
  } = {}) {
    const defaultSourceMap = new Map();

    function addCountries(list, sourceKey) {
      for (const iso3 of list || []) {
        if (!iso3) continue;
        const existing = defaultSourceMap.get(iso3) || [];
        if (!existing.includes(sourceKey)) {
          defaultSourceMap.set(iso3, [...existing, sourceKey]);
        }
      }
    }

    addCountries(fatfBlackList, "fatfBlack");
    addCountries(fatfGreyList, "fatfGrey");
    addCountries(ofacProgramsList, "ofacPrograms");
    addCountries(taxHavensList, "taxHavens");
    addCountries(euHighRiskList, "euHighRisk");

    const next = { standard: [], medium: [], high: [] };
    for (const [iso3, sourceKeys] of defaultSourceMap.entries()) {
      const risk = countryRiskFromSourceKeys(rules, sourceKeys);
      next[risk].push(iso3);
    }

    return {
      standard: uniqIso3(next.standard),
      medium: uniqIso3(next.medium),
      high: uniqIso3(next.high),
    };
  }

  function prefillCountryListsFromDefault() {
    setCustomStdText(generalCountryStdText);
    setCustomMedText(generalCountryMedText);
    setCustomHighText(generalCountryHighText);
    setCountryUseGeneralList(false);
  }

  async function saveSupportingDocumentPolicy() {
    if (savingSupportingDocs || !selectedOrgId || !supportingDocsDirty) return;
    setSavingSupportingDocs(true);
    try {
      await assertAccessOrThrow();
      if (isDefaultRiskSelection) {
        const cleanPolicy = sanitizeSupportingDocumentPolicy(supportingDocPolicy);
        const cleanUboDocumentScope = normalizeUboDocumentScope(supportingDocUboDocumentScope);
        await setDoc(
          doc(db, SUPPORTING_DOCUMENT_POLICY_COLLECTION, "lists"),
          {
            sow: cleanPolicy,
            uboDocumentScope: cleanUboDocumentScope,
            updatedAt: serverTimestamp(),
          },
          { merge: true }
        );
        setSupportingDocPolicy(cleanPolicy);
        setGeneralSupportingDocPolicy(cleanPolicy);
        setSupportingDocUboDocumentScope(cleanUboDocumentScope);
        setGeneralSupportingDocUboDocumentScope(cleanUboDocumentScope);
      } else {
        const cleanPolicy = sanitizeSupportingDocumentPolicy(supportingDocPolicy);
        const cleanUboDocumentScope = normalizeUboDocumentScope(supportingDocUboDocumentScope);
        await setDoc(
          doc(db, SUPPORTING_DOCUMENT_POLICY_COLLECTION, selectedOrgId),
          {
            useGeneralList: supportingDocUseGeneralList,
            sow: cleanPolicy,
            uboDocumentScope: cleanUboDocumentScope,
            updatedAt: serverTimestamp(),
          },
          { merge: true }
        );
        setSupportingDocPolicy(cleanPolicy);
        setSupportingDocUboDocumentScope(cleanUboDocumentScope);
      }
      setSavedSnapshots((prev) => ({
        ...prev,
        supportingDocs: supportingDocsSnapshot,
      }));
      toastRef.current?.show({ severity: "success", summary: "Saved", detail: "Supporting document policy updated.", life: 2500 });
    } catch (e) {
      toastRef.current?.show({ severity: "error", summary: "Save failed", detail: e?.message || "Failed to save supporting document policy", life: 4500 });
    } finally {
      setSavingSupportingDocs(false);
    }
  }

  function prefillSupportingDocumentPolicyFromDefault() {
    setSupportingDocPolicy(sanitizeSupportingDocumentPolicy(generalSupportingDocPolicy));
    setSupportingDocUboDocumentScope(generalSupportingDocUboDocumentScope);
    setSupportingDocUseGeneralList(false);
  }

  async function saveRelationshipRiskRules() {
    if (savingRelationshipRiskRules || !selectedOrgId || !relationshipRiskDirty) return;
    setSavingRelationshipRiskRules(true);
    try {
      await assertAccessOrThrow();
      const cleanRules = sanitizeRelationshipRiskSettings(relationshipRiskRules);
      if (isDefaultRiskSelection) {
        await setDoc(
          doc(db, RELATIONSHIP_RISK_SETTINGS_COLLECTION, RELATIONSHIP_RISK_DEFAULT_DOC_ID),
          {
            rules: cleanRules,
            updatedAt: serverTimestamp(),
          },
          { merge: true }
        );
        setGeneralRelationshipRiskRules(cleanRules);
      } else {
        await setDoc(
          doc(db, RELATIONSHIP_RISK_SETTINGS_COLLECTION, selectedOrgId),
          {
            useGeneralSettings: relationshipRiskUseGeneralSettings,
            useGeneralModel: relationshipRiskUseGeneralModel,
            rules: cleanRules,
            updatedAt: serverTimestamp(),
          },
          { merge: true }
        );
      }
      setRelationshipRiskRules(cleanRules);
      setSavedSnapshots((prev) => ({
        ...prev,
        relationshipRisk: relationshipRiskSnapshot,
      }));
      toastRef.current?.show({ severity: "success", summary: "Saved", detail: "Relationship risk rules updated.", life: 2500 });
    } catch (e) {
      toastRef.current?.show({ severity: "error", summary: "Save failed", detail: e?.message || "Failed to save relationship risk rules", life: 4500 });
    } finally {
      setSavingRelationshipRiskRules(false);
    }
  }

  function prefillRelationshipRiskRulesFromDefault() {
    setRelationshipRiskRules(sanitizeRelationshipRiskSettings(generalRelationshipRiskRules));
    setRelationshipRiskUseGeneralSettings(false);
    setRelationshipRiskUseGeneralModel(false);
  }

  // ---- Org label for subtitle ----
  const selectedOrgName = useMemo(() => {
    if (selectedOrgId === DEFAULT_RISK_OPTION_ID) return "Default";
    if (isManager) return managerOrgName || selectedOrgId;
    const found = allOrgs.find((o) => o.orgId === selectedOrgId);
    return found ? found.name : selectedOrgId;
  }, [isManager, managerOrgName, selectedOrgId, allOrgs]);

  // ******************* RENDER HTML ********************

  return (
    <motion.div
      variants={pageAnim}
      initial="initial"
      animate="animate"
      exit="exit"
      transition={{ duration: 0.25 }}
      className="adminUsersPage"
    >
      {/* TOP BAR */}
      <div className="adminUsersTitleBar">
        <div className="container py-3">
          <div className="clientsHeader d-flex align-items-center justify-content-between flex-wrap gap-2">
            <div>
              <h2 className="clientsHeaderTitle m-0">Risk Settings</h2>
              <div className="small" style={{ color: "#fff", opacity: 0.8 }}>
                {selectedOrgName
                  ? `Editing: ${selectedOrgName}`
                  : "Country risk lists, industry risk lists and internal flags"}
              </div>
            </div>

            <div className="d-flex align-items-center gap-2 flex-wrap">
              {/* Admin org selector */}
              {isAdmin && allOrgs.length > 0 && (
                <Select
                  styles={orgSelectStyles}
                  options={[
                    { value: DEFAULT_RISK_OPTION_ID, label: "Default" },
                    ...allOrgs.map((o) => ({ value: o.orgId, label: o.name })),
                  ]}
                  value={selectedOrgOption}
                  onChange={(opt) => {
                    setSelectedOrgOption(opt);
                    setSelectedOrgId(opt?.value || "");
                  }}
                  placeholder="Search organisation…"
                  isClearable={false}
                  style={{ width: 220 }}
                />
              )}

              <button
                type="button"
                className="btn btn-sm btn-outline-light"
                onClick={() => nav("/clients")}
                style={{ whiteSpace: "nowrap" }}
              >
                ← Back
              </button>

              <button
                type="button"
                className="btn btn-sm btn-outline-light"
                onClick={loadLists}
                disabled={loading || !selectedOrgId}
              >
                <i className="bi bi-arrow-repeat me-2" />
                Refresh
              </button>

              <button
                type="button"
                className="btn btn-sm btn-outline-light"
                onClick={handleLogout}
                style={{ whiteSpace: "nowrap" }}
              >
                <i className="bi bi-box-arrow-right me-2" aria-hidden="true" />
                Log out
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* CONTENT */}
      <div className="container py-4">
        {err && <div className="alert alert-danger">{err}</div>}

        {!selectedOrgId && !loading && !err && (
          <div className="card p-4 text-muted">
            {isAdmin ? "Select an organisation above to load its risk settings." : "No organisation found."}
          </div>
        )}

        {loading && selectedOrgId ? (
          <div className="card p-4 text-muted">Loading…</div>
        ) : (
          selectedOrgId && !loading && (
            <>
              <RiskLevelModelCard
                isDefaultRiskSelection={isDefaultRiskSelection}
                rules={relationshipRiskRules}
                defaultRules={generalRelationshipRiskRules}
                setRules={setRelationshipRiskRules}
                useDefaultModel={!isDefaultRiskSelection && relationshipRiskUseGeneralModel}
                setUseDefaultModel={setRelationshipRiskUseGeneralModel}
                useDefaultRules={!isDefaultRiskSelection && relationshipRiskUseGeneralSettings}
                onSave={saveRelationshipRiskRules}
                saving={savingRelationshipRiskRules}
                canSave={relationshipRiskDirty}
              />

              {/* LISTS CARD */}
              <div className="card shadow-sm mb-3">
                <div className="p-3 border-bottom d-flex align-items-start justify-content-between gap-3 flex-wrap">
                  <div>
                    <div className="fw-bold" style={{ color: "#4C5465" }}>
                      Country Risk
                    </div>
                    <div className="small text-muted">
                      <span className="d-block">
                        One country per line. Stored in{" "}
                        <code>
                          {isDefaultRiskSelection
                            ? "countryRisk/lists"
                            : `countryRisk/${selectedOrgId}`}
                        </code>
                        .
                      </span>
                      {!isDefaultRiskSelection ? (
                        <span className="d-block">
                          If the general list is enabled, categorisation uses countryRisk/lists.
                        </span>
                      ) : null}
                    </div>
                  </div>

                  <div>
                    <div
                      className="relationshipRiskModelToggle"
                      role="radiogroup"
                      aria-label="Entity main country of operation"
                    >
                      <div className="relationshipRiskModelToggleHeading">
                        Use main country of operation for entity clients
                      </div>
                      {[
                        {
                          value: false,
                          label: "Not used",
                          detail: "Current entity layout",
                        },
                        {
                          value: true,
                          label: "Used",
                          detail: "Show operation country",
                        },
                      ].map((option) => (
                        <label
                          key={String(option.value)}
                          className={`relationshipRiskModelOption ${
                            countryUseMainCountryOfOperation === option.value
                              ? "is-selected"
                              : ""
                          }`}
                        >
                          <input
                            type="radio"
                            name="country-use-main-country-operation"
                            checked={countryUseMainCountryOfOperation === option.value}
                            onChange={() => setCountryUseMainCountryOfOperation(option.value)}
                          />
                          <span>
                            <span className="relationshipRiskModelOptionTitle">
                              {option.label}
                            </span>
                            <span className="relationshipRiskModelOptionDetail">
                              {option.detail}
                            </span>
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="p-3">
                  <CountryRiskRulesPanel
                    rules={countryRiskRules}
                    setRules={setCountryRiskRules}
                    disabled={!canEditGlobalCountryLists}
                    open={countryRiskRulesOpen}
                    onToggle={() => setCountryRiskRulesOpen((open) => !open)}
                    isTwoLevelFinalRiskModel={isTwoLevelFinalRiskModel}
                  />

                  <div
                    className="p-3 rounded-3 border"
                    style={{ background: "#F8FAF9", borderColor: "#E2E8E4" }}
                  >
                    <button
                      type="button"
                      className={`riskExpandableHeader riskExpandableHeader--inset${
                        defaultGlobalListsOpen
                          ? " riskExpandableHeader--open mb-3 pb-2"
                          : ""
                      }`}
                      aria-expanded={defaultGlobalListsOpen}
                      onClick={() => setDefaultGlobalListsOpen((open) => !open)}
                      style={{ borderColor: "#E2E8E4" }}
                    >
                      <div>
                        <div className="fw-semibold" style={{ color: "#4C5465", fontSize: 14 }}>
                          Country lists
                        </div>
                        <div className="small text-muted">OFAC, UN, UAE, EU, Custom</div>
                      </div>
                      <RiskExpandableIcon open={defaultGlobalListsOpen} />
                    </button>

                    <AnimatePresence initial={false}>
                      {defaultGlobalListsOpen && (
                        <AnimatedCollapse>
                          <div className="row g-3">
                            <CountryRiskListEditor
                              title="FATF Grey list"
                              count={fatfGrey.length}
                              text={fatfGreyText}
                              options={countryOptions}
                              onTextChange={setFatfGreyText}
                              disabled={!canEditGlobalCountryLists}
                              isAdmin={isAdmin}
                              advancedOpen={!!countryAdvancedOpen.fatfGrey}
                              advancedKey="fatfGrey"
                              onToggleAdvanced={toggleCountryAdvanced}
                              overrideMap={countryOverrideMapByDefaultList.fatfGrey}
                            />

                            <CountryRiskListEditor
                              title="FATF Black list"
                              count={fatfBlack.length}
                              text={fatfBlackText}
                              options={countryOptions}
                              onTextChange={setFatfBlackText}
                              disabled={!canEditGlobalCountryLists}
                              isAdmin={isAdmin}
                              advancedOpen={!!countryAdvancedOpen.fatfBlack}
                              advancedKey="fatfBlack"
                              onToggleAdvanced={toggleCountryAdvanced}
                              overrideMap={countryOverrideMapByDefaultList.fatfBlack}
                            />

                            <CountryRiskListEditor
                              title="OFAC sanctions"
                              count={ofacPrograms.length}
                              text={ofacProgramsText}
                              options={countryOptions}
                              onTextChange={setOfacProgramsText}
                              disabled={!canEditGlobalCountryLists}
                              isAdmin={isAdmin}
                              advancedOpen={!!countryAdvancedOpen.ofacPrograms}
                              advancedKey="ofacPrograms"
                              onToggleAdvanced={toggleCountryAdvanced}
                              overrideMap={countryOverrideMapByDefaultList.ofacPrograms}
                            />

                            <CountryRiskListEditor
                              title="EU Tax havens"
                              count={taxHavens.length}
                              text={taxHavensText}
                              options={countryOptions}
                              onTextChange={setTaxHavensText}
                              disabled={!canEditGlobalCountryLists}
                              isAdmin={isAdmin}
                              advancedOpen={!!countryAdvancedOpen.taxHavens}
                              advancedKey="taxHavens"
                              onToggleAdvanced={toggleCountryAdvanced}
                              overrideMap={countryOverrideMapByDefaultList.taxHavens}
                            />

                            <CountryRiskListEditor
                              title="EU High-Risk Third Countries"
                              count={euHighRisk.length}
                              text={euHighRiskText}
                              options={countryOptions}
                              onTextChange={setEuHighRiskText}
                              disabled={!canEditGlobalCountryLists}
                              isAdmin={isAdmin}
                              advancedOpen={!!countryAdvancedOpen.euHighRisk}
                              advancedKey="euHighRisk"
                              onToggleAdvanced={toggleCountryAdvanced}
                              overrideMap={countryOverrideMapByDefaultList.euHighRisk}
                            />
                          </div>
                        </AnimatedCollapse>
                      )}
                    </AnimatePresence>
                  </div>

                  <div
                    className="p-3 rounded-3 border mt-3"
                    style={{ background: "#FFFDFC", borderColor: "#E7DED8" }}
                  >
                    <div
                      className="d-flex align-items-center justify-content-between gap-2 mb-3 pb-2 border-bottom"
                      style={{ borderColor: "#E7DED8" }}
                    >
                      <div className="fw-semibold" style={{ color: "#4C5465", fontSize: 14 }}>
                        {isDefaultRiskSelection ? "Custom country categorisation (default)" : "Organisation custom lists"}
                      </div>
                      <span className="text-muted" style={{ fontSize: 12 }}>
                        {isDefaultRiskSelection ? "Editing default" : selectedOrgName}
                      </span>
                    </div>

                    <div className="row g-3">
                      <CountryRiskListEditor
                        title={isDefaultRiskSelection ? "Default • Standard" : "Custom • Standard"}
                        count={displayedCountryStandard.length}
                        text={displayedCountryStdText}
                        options={countryOptions}
                        onTextChange={setCustomStdText}
                        disabled={!isDefaultRiskSelection && countryUseGeneralList}
                        isAdmin={isAdmin}
                        advancedOpen={!!countryAdvancedOpen.customStandard}
                        advancedKey="customStandard"
                        onToggleAdvanced={toggleCountryAdvanced}
                        overrideMap={countryOverrideMapByRisk.standard}
                      />

                      <CountryRiskListEditor
                        title={isDefaultRiskSelection ? "Default • Medium" : "Custom • Medium"}
                        count={displayedCountryMedium.length}
                        text={displayedCountryMedText}
                        options={countryOptions}
                        onTextChange={setCustomMedText}
                        disabled={!isDefaultRiskSelection && countryUseGeneralList}
                        isAdmin={isAdmin}
                        advancedOpen={!!countryAdvancedOpen.customMedium}
                        advancedKey="customMedium"
                        onToggleAdvanced={toggleCountryAdvanced}
                        overrideMap={countryOverrideMapByRisk.medium}
                        attention={isTwoLevelFinalRiskModel}
                        inputBackground={isTwoLevelFinalRiskModel ? "#fffbf7" : ""}
                      />

                      <CountryRiskListEditor
                        title={isDefaultRiskSelection ? "Default • High" : "Custom • High"}
                        count={displayedCountryHigh.length}
                        text={displayedCountryHighText}
                        options={countryOptions}
                        onTextChange={setCustomHighText}
                        disabled={!isDefaultRiskSelection && countryUseGeneralList}
                        isAdmin={isAdmin}
                        advancedOpen={!!countryAdvancedOpen.customHigh}
                        advancedKey="customHigh"
                        onToggleAdvanced={toggleCountryAdvanced}
                        overrideMap={countryOverrideMapByRisk.high}
                      />

                      {!isDefaultRiskSelection && !countryUseGeneralList && (
                        <CountryOverrideCompactCard
                          rows={countryOverrideRows}
                          downgradeCount={countryOverrideDowngradeCount}
                        />
                      )}
                    </div>
                  </div>

                  <div className="d-flex w-100 justify-content-between align-items-end gap-3 mt-3 flex-wrap">
                    <div className="d-flex flex-column gap-2">
                      {!isDefaultRiskSelection ? (
                        <div className="riskDocDefaultPolicyToggle riskDocDefaultPolicyToggle--withAction">
                          <label htmlFor="countryUseGeneralList" className="riskDocDefaultPolicyToggleLabel">
                            <input
                              id="countryUseGeneralList"
                              className="form-check-input m-0 adminRiskSettingsCheckbox"
                              type="checkbox"
                              checked={countryUseGeneralList}
                              onChange={(e) => setCountryUseGeneralList(e.target.checked)}
                            />
                            <span>Use default country risk categorisation</span>
                          </label>
                          {!countryUseGeneralList ? (
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-secondary"
                              onClick={prefillCountryListsFromDefault}
                              disabled={savingLists}
                            >
                              Prefill default list
                            </button>
                          ) : null}
                        </div>
                      ) : (
                        <span className="small text-muted">
                          Editing default country risk categorisation
                        </span>
                      )}
                    </div>

                    <div className="ms-auto">
                      <button
                        type="button"
                        className="btn btn-save1"
                        onClick={saveCountryLists}
                        disabled={savingLists || !countryDirty}
                      >
                        {savingLists ? "Saving…" : "Save country lists"}
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* INDUSTRY CARD */}
              <div className="card shadow-sm mb-3">
                <div className="p-3 border-bottom">
                  <div className="fw-bold" style={{ color: "#4C5465" }}>
                    Business Activity Risk
                  </div>
                  <div className="small text-muted">
                    One category per line. Stored in{" "}
                    <code>
                      {isDefaultRiskSelection
                        ? "industryRisk/lists"
                        : `industryRisk/${selectedOrgId}`}
                    </code>
                    {!isDefaultRiskSelection &&
                      ". If the general list is enabled, categorisation uses industryRisk/lists."}
                  </div>
                </div>

                <div className="p-3">
                  <div className="row g-3">
                    <div className="col">
                      <div className="text-muted small mb-1">
                        Standard ({displayedIndustryStandard.length})
                      </div>
                      <textarea
                        className="form-control industryRiskTextarea"
                        rows={8}
                        value={displayedIndStdText}
                        onChange={(e) => setIndStdText(e.target.value)}
                        disabled={industryUseGeneralList}
                      />
                    </div>

                    <div
                      className={`col ${
                        isTwoLevelFinalRiskModel ? "riskInputAttention riskInputAttention--standardBorder" : ""
                      }`}
                    >
                      <div className="text-muted small mb-1">
                        Medium ({displayedIndustryMedium.length})
                        {isTwoLevelFinalRiskModel ? (
                          <span className="riskInputAttentionBadge">Input factor</span>
                        ) : null}
                      </div>
                      <textarea
                        className="form-control industryRiskTextarea"
                        rows={8}
                        value={displayedIndMedText}
                        onChange={(e) => setIndMedText(e.target.value)}
                        disabled={industryUseGeneralList}
                      />
                    </div>

                    <div className="col">
                      <div className="text-muted small mb-1">
                        High ({displayedIndustryHigh.length})
                      </div>
                      <textarea
                        className="form-control industryRiskTextarea"
                        rows={8}
                        value={displayedIndHighText}
                        onChange={(e) => setIndHighText(e.target.value)}
                        disabled={industryUseGeneralList}
                      />
                    </div>
                  </div>

                  <div className="d-flex align-items-center justify-content-between gap-2 mt-3 flex-wrap">
                    {!isDefaultRiskSelection ? (
                      <div className="riskDocDefaultPolicyToggle riskDocDefaultPolicyToggle--withAction">
                        <label htmlFor="industryUseGeneralList" className="riskDocDefaultPolicyToggleLabel">
                          <input
                            id="industryUseGeneralList"
                            className="form-check-input m-0 adminRiskSettingsCheckbox"
                            type="checkbox"
                            checked={industryUseGeneralList}
                            onChange={(e) => setIndustryUseGeneralList(e.target.checked)}
                          />
                          <span>Use default industry risk categorisation</span>
                        </label>
                        {!industryUseGeneralList ? (
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary"
                            onClick={prefillIndustryListsFromDefault}
                            disabled={savingIndustry}
                          >
                            Prefill default list
                          </button>
                        ) : null}
                      </div>
                    ) : (
                      <span className="small text-muted">
                        Editing default industry risk categorisation
                      </span>
                    )}
                    <div className="d-flex align-items-center gap-2 flex-wrap">
                      <button
                        type="button"
                        className="btn btn-save1"
                        onClick={saveIndustryLists}
                        disabled={savingIndustry || !industryDirty}
                      >
                        {savingIndustry ? "Saving…" : "Save industry lists"}
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <SupportingDocumentsPolicyCard
                isDefaultRiskSelection={isDefaultRiskSelection}
                selectedOrgId={selectedOrgId}
                policy={displayedSupportingDocPolicy}
                setPolicy={setSupportingDocPolicy}
                relationshipRiskModel={activeFinalRiskModel}
                uboDocumentScope={displayedSupportingDocUboDocumentScope}
                setUboDocumentScope={setSupportingDocUboDocumentScope}
                useDefaultPolicy={!isDefaultRiskSelection && supportingDocUseGeneralList}
                setUseDefaultPolicy={setSupportingDocUseGeneralList}
                canPrefillDefault={!isDefaultRiskSelection && !supportingDocUseGeneralList}
                onPrefillDefault={prefillSupportingDocumentPolicyFromDefault}
                onSave={saveSupportingDocumentPolicy}
                saving={savingSupportingDocs}
                canSave={supportingDocsDirty}
              />

              <RelationshipRiskRulesCard
                isDefaultRiskSelection={isDefaultRiskSelection}
                selectedOrgId={selectedOrgId}
                rules={displayedRelationshipRiskRules}
                setRules={setRelationshipRiskRules}
                activeModelOverride={activeFinalRiskModel}
                useDefaultRules={!isDefaultRiskSelection && relationshipRiskUseGeneralSettings}
                setUseDefaultRules={setRelationshipRiskUseGeneralSettings}
                canPrefillDefault={!isDefaultRiskSelection && !relationshipRiskUseGeneralSettings}
                onPrefillDefault={prefillRelationshipRiskRulesFromDefault}
                onSave={saveRelationshipRiskRules}
                saving={savingRelationshipRiskRules}
                canSave={relationshipRiskDirty}
              />
            </>
          )
        )}
      </div>

      <Toast ref={toastRef} />
    </motion.div>
  );
}
